import { useState, useEffect } from "react";
import { usePagedList } from "@/hooks/usePagedList";
import Pagination from "@/components/Pagination";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Calendar, DollarSign, Users, Download, Eye, Pencil, ClipboardList, Trash2, ChevronDown, ChevronUp, Upload, CheckCircle2, Check, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import DatePicker from "@/components/DatePicker";
import BulkImportPreview from "@/components/BulkImportPreview";
import { pickableContacts, contactLabel } from "../components/contacts/ContactsTable";

// Highest "YY-NNN" project number in the current year's series; falls back to the overall highest.
const getLastProjectNumber = (projects) => {
  const parsed = (projects || [])
    .map((p) => {
      const m = /^(\d{2})-(\d+)$/.exec((p.project_number || "").trim());
      return m ? { yy: Number(m[1]), seq: Number(m[2]), raw: p.project_number.trim() } : null;
    })
    .filter(Boolean);
  if (!parsed.length) return null;
  const yy = new Date().getFullYear() % 100;
  const pool = parsed.some((x) => x.yy === yy) ? parsed.filter((x) => x.yy === yy) : parsed;
  return pool.reduce((best, x) => (x.yy > best.yy || (x.yy === best.yy && x.seq > best.seq) ? x : best)).raw;
};

// Increments the last number keeping its padding, e.g. "26-115" -> "26-116".
const getNextProjectNumber = (last) => {
  const m = /^(\d{2})-(\d+)$/.exec(last || "");
  return m ? `${m[1]}-${String(Number(m[2]) + 1).padStart(m[2].length, "0")}` : null;
};

const isDuplicateProjectNumber = (projects, value) => {
  const v = (value || "").trim().toLowerCase();
  return !!v && (projects || []).some((p) => (p.project_number || "").trim().toLowerCase() === v);
};

export default function Projects({ user }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [estimations, setEstimations] = useState([]);
  const [estimationPickerOpen, setEstimationPickerOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);
  
  // Submittals state
  const [submittalsDialogOpen, setSubmittalsDialogOpen] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  // Full project record for the currently-open Submittals dialog.
  // Kept separate from the sliced list row (which drops scope_of_work).
  const [currentProjectFull, setCurrentProjectFull] = useState(null);
  // Estimation sync dialog state
  const [syncDialog, setSyncDialog] = useState({
    open: false, loading: false, saving: false, error: null,
    project: null, candidate: null,
  });
  const [submittals, setSubmittals] = useState([]);
  const [editedSubmittals, setEditedSubmittals] = useState([]); // Track local changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [expandedSubmittal, setExpandedSubmittal] = useState(null);
  const [loadingSubmittals, setLoadingSubmittals] = useState(false);
  const [currentClientBilling, setCurrentClientBilling] = useState({ approval: 0, fabrication: 0 });
  
  // Force-Complete dialogs
  const [forceCompleteDialog, setForceCompleteDialog] = useState({ open: false, submittalId: null, note: "" });
  const [viewForceCompleteDialog, setViewForceCompleteDialog] = useState({ open: false, submittal: null });
  
  // Bulk import preview state
  const [importPreviewOpen, setImportPreviewOpen] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState(null);

  // RBAC-based permission helpers using user.permissions.projects
  const canCreate = () => user?.permissions?.projects?.create === true;
  const canEdit = () => user?.permissions?.projects?.edit === true;
  const canDelete = () => user?.permissions?.projects?.delete === true;
  const canExport = () => user?.permissions?.projects?.export === true;
  const canApprove = () => user?.permissions?.projects?.approve === true;

  // Check if user is a Projects department user (Team Member, Team Lead, or Manager)
  // These users have restricted access to project management features
  const isProjectsDeptUser = () => {
    return user?.department === "Projects" && 
           ["Team Member", "Team Lead", "Manager"].includes(user?.access_level);
  };

  // Helper function to check if user can view cost information
  // This is based on department for business reasons (cost visibility is sensitive)
  const canViewCosts = () => {
    return user?.department === "Management" || 
           user?.department === "Sales" || 
           user?.department === "PMO" ||
           user?.department === "Accounts and Finance";
  };


  // Calculate end date based on start date and schedule (5-day work week)
  // Schedule is INCLUSIVE of start date (start date = day 1)
  const calculateEndDate = (startDate, weeks, days) => {
    if (!startDate || (weeks === "" && days === "")) return "";
    
    const totalDays = (parseInt(weeks) || 0) * 5 + (parseInt(days) || 0);
    if (totalDays === 0) return "";
    
    const start = new Date(startDate);
    
    // Check if start date is a weekend
    const startDayOfWeek = start.getDay();
    if (startDayOfWeek === 0 || startDayOfWeek === 6) {
      // Start date is a weekend, which shouldn't happen but handle it
      return startDate;
    }
    
    // Start date counts as day 1, so we need to add (totalDays - 1) more working days
    let currentDate = new Date(start);
    let workDaysAdded = 1; // Start date is day 1
    
    // Add remaining working days (skip weekends)
    while (workDaysAdded < totalDays) {
      currentDate.setDate(currentDate.getDate() + 1);
      const dayOfWeek = currentDate.getDay();
      
      // Skip Saturday (6) and Sunday (0)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workDaysAdded++;
      }
    }
    
    // Format as YYYY-MM-DD for input[type="date"]
    return currentDate.toISOString().split('T')[0];
  };

  const getDefaultScopes = () => [
    { id: crypto.randomUUID(), name: "Anchor Bolt", weightage: "", total_weightage: 0 },
    { id: crypto.randomUUID(), name: "Embeds", weightage: "", total_weightage: 0 },
    { id: crypto.randomUUID(), name: "Structural", weightage: "", total_weightage: 0 },
    { id: crypto.randomUUID(), name: "Miscellaneous", weightage: "", total_weightage: 0 }
  ];

  const [viewingProject, setViewingProject] = useState(null);  // Store full project for approval status
  const [formData, setFormData] = useState({
    project_number: "",
    client_project_number: "",
    name: "",
    description: "",
    client_id: "",
    estimation_id: "",
    estimation_number: "",
    estimated_hours: "",
    allocated_hours: "",
    base_price: "",
    detailing_cost: "",
    engineering_cost: "",
    currency: "USD",
    schedule_weeks: "",
    schedule_days: "",
    allocated_schedule_weeks: "",
    allocated_schedule_days: "",
    awarded_date: "",
    sales: "",
    branch: [],  // Changed to array for multiple branches
    branch_data: {},  // Branch allocation data for multi-branch projects
    purchase_order_number: "",
    start_date: "",
    end_date: "",
    assigned_users: [],
    scope_of_work: getDefaultScopes(),  // Default 5 scopes
    is_old_project: false,  // Flag for mid-progress projects
    hours_already_spent: "",  // Hours spent before deployment (empty by default)
    project_contact_id: "",
    billing_contact_id: "",
    client_notes_project_start: ""  // Additional notes from client at project start
  });

  // Branch allocation state for multi-branch projects
  const [branchAllocationDialogOpen, setBranchAllocationDialogOpen] = useState(false);
  const [branchAllocations, setBranchAllocations] = useState({});
  const [allocationErrors, setAllocationErrors] = useState({});

  // Filter states
  const [filters, setFilters] = useState({
    client_name: "",
    project_number: "",
    branch: "",
    sales: "",
    estimation_number: "",
    status: ""
  });

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    fetchClients();
    fetchEstimations();
    fetchBranches();
  }, []);

  // Fetch contacts when client changes
  useEffect(() => {
    if (formData.client_id) {
      fetchContactsForClient(formData.client_id);
    } else {
      setContacts([]);
    }
  }, [formData.client_id]);


  // Auto-calculate end date when start date or estimated schedule changes
  useEffect(() => {
    if (formData.start_date && (formData.schedule_weeks || formData.schedule_days)) {
      const calculatedEndDate = calculateEndDate(
        formData.start_date,
        formData.schedule_weeks,
        formData.schedule_days
      );
      if (calculatedEndDate && calculatedEndDate !== formData.end_date) {
        setFormData(prev => ({ ...prev, end_date: calculatedEndDate }));
      }
    }
  }, [formData.start_date, formData.schedule_weeks, formData.schedule_days]);

  const fetchBranches = async () => {
    try {
      const response = await api.get("/branches");
      setBranches(response.data || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
    }
  };

  const [awardSyncIds, setAwardSyncIds] = useState(new Set());

  const fetchProjects = async () => {
    try {
      const response = await api.get("/projects");
      // Sort descending by project_number (newest first) — numeric-aware so
      // "25-0123" > "25-99" and "25-100" > "25-99".
      const sorted = [...(response.data || [])].sort((a, b) =>
        (b.project_number || "").localeCompare(a.project_number || "", undefined, { numeric: true, sensitivity: "base" })
      );
      setProjects(sorted);
      api.get("/projects-award-sync-pending")
        .then((r) => setAwardSyncIds(new Set(r.data?.project_ids || [])))
        .catch(() => setAwardSyncIds(new Set()));
    } catch (error) {
      toast.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients");
    }
  };

  const fetchContactsForClient = async (clientId) => {
    try {
      const response = await api.get(`/contacts?client_id=${clientId}`);
      setContacts(response.data);
    } catch (error) {
      console.error("Failed to fetch contacts");
      setContacts([]);
    }
  };

  const fetchEstimations = async () => {
    try {
      const response = await api.get("/estimations");
      setEstimations(response.data);
    } catch (error) {
      console.error("Failed to fetch estimations");
    }
  };

  const fetchAvailableEstimations = async () => {
    try {
      const response = await api.get("/estimations/available-for-project");
      setEstimations(response.data);
    } catch (error) {
      console.error("Failed to fetch available estimations");
    }
  };

  const handleEstimationSelect = (estimationId) => {
    if (!estimationId) return;
    
    const estimation = estimations.find(e => e.id === estimationId);
    if (estimation) {
      // Phase 2 — pull only the AWARDED portion into the project.
      // Fall back to full estimation totals for legacy records without line-item awards.
      const awardedHours = Number(estimation.awarded_hours ?? 0);
      const awardedValue = Number(estimation.awarded_value ?? 0);
      const hasAwards = awardedHours > 0 || awardedValue > 0;

      const estimatedHoursNum = hasAwards ? awardedHours : (Number(estimation.estimated_hours) || 0);
      const totalValue = hasAwards ? awardedValue : ((estimation.detailing_cost || 0) + (estimation.engineering_cost || 0));
      // Awarded cost split (only compute from awarded lines when available)
      let detailingCost = Number(estimation.detailing_cost || 0);
      let engineeringCost = Number(estimation.engineering_cost || 0);
      if (hasAwards && Array.isArray(estimation.line_items)) {
        detailingCost = estimation.line_items
          .filter(li => (li.award_status || "pending").toLowerCase() === "awarded")
          .reduce((sum, li) => sum + Number(li.detailing_cost || 0), 0);
        engineeringCost = estimation.line_items
          .filter(li => (li.award_status || "pending").toLowerCase() === "awarded")
          .reduce((sum, li) => sum + Number(li.engineering_cost || 0), 0);
      }

      // Schedule: when partially awarded, sum durations from awarded line items only.
      // Normalize days overflow into weeks using the backend convention (5 days = 1 week).
      let scheduleWeeksNum = Number(estimation.schedule_weeks || 0);
      let scheduleDaysNum = Number(estimation.schedule_days || 0);
      if (hasAwards && Array.isArray(estimation.line_items)) {
        const awardedLines = estimation.line_items.filter(
          li => (li.award_status || "pending").toLowerCase() === "awarded"
        );
        const totalDays = awardedLines.reduce(
          (sum, li) => sum + (Number(li.duration_weeks || 0) * 5) + Number(li.duration_days || 0),
          0
        );
        // Fall back to overall schedule if line items have no durations (legacy data).
        if (totalDays > 0 || awardedLines.some(li => li.duration_weeks || li.duration_days)) {
          scheduleWeeksNum = Math.floor(totalDays / 5);
          scheduleDaysNum = totalDays % 5;
        }
      }
      const scheduleWeeks = scheduleWeeksNum.toString();
      const scheduleDays = scheduleDaysNum.toString();
      const estimatedHours = estimatedHoursNum ? estimatedHoursNum.toString() : "";
      
      setFormData({
        ...formData,
        estimation_id: estimationId,
        estimation_number: estimation.estimation_number || "",
        name: estimation.project_name || "",
        client_id: estimation.client_id || "",
        estimated_hours: estimatedHours,
        allocated_hours: estimatedHours, // Initialize with awarded hours
        base_price: totalValue.toString(),
        detailing_cost: detailingCost.toString(),
        engineering_cost: engineeringCost.toString(),
        currency: estimation.currency || "USD",
        schedule_weeks: scheduleWeeks,
        schedule_days: scheduleDays,
        allocated_schedule_weeks: scheduleWeeks, // Initialize with same value as estimated
        allocated_schedule_days: scheduleDays, // Initialize with same value as estimated
        awarded_date: estimation.awarded_date || "",
        sales: estimation.sales || ""
      });
    }
  };

  // Filter projects
  const filteredProjects = projects.filter(project => {
    // Client name filter - search in client name only (not number)
    if (filters.client_name) {
      const client = clients.find(c => c.id === project.client_id);
      const clientName = client ? (client.client_name || client.company || "") : (project.client_name || "");
      if (!clientName.toLowerCase().includes(filters.client_name.toLowerCase())) {
        return false;
      }
    }
    
    // Project number filter - search in project number OR project name
    if (filters.project_number) {
      const projectNumber = project.project_number || project.id.substring(0, 8) || "";
      const projectName = project.name || "";
      const searchTerm = filters.project_number.toLowerCase();
      if (!projectNumber.toLowerCase().includes(searchTerm) && !projectName.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    
    // Check if project has any of the selected branch (for multiple branches)
    if (filters.branch) {
      const projectBranches = Array.isArray(project.branch) ? project.branch : [project.branch];
      if (!projectBranches.includes(filters.branch)) return false;
    }
    if (filters.sales && project.sales !== filters.sales) return false;
    if (filters.estimation_number && (!project.estimation_number || !project.estimation_number.toLowerCase().includes(filters.estimation_number.toLowerCase()))) return false;
    if (filters.status && (project.status || "yet_to_start") !== filters.status) return false;
    return true;
  });
  const paged = usePagedList(filteredProjects);

  const clearFilters = () => {
    setFilters({
      client_name: "",
      project_number: "",
      branch: "",
      sales: "",
      estimation_number: "",
      status: ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editMode && isDuplicateProjectNumber(projects, formData.project_number)) {
      toast.error(`Project number ${formData.project_number.trim()} already exists`);
      return;
    }
    
    // Validate estimation is selected only for create mode
    if (!editMode && !formData.estimation_id) {
      toast.error("Please select an estimation");
      return;
    }
    
    // Validate at least one branch is selected
    if (!formData.branch || formData.branch.length === 0) {
      toast.error("Please select at least one branch");
      return;
    }
    
    // Validate allocated hours (allow up to 150% of estimated hours)
    const estimatedHours = parseFloat(formData.estimated_hours) || 0;
    const allocatedHours = parseFloat(formData.allocated_hours) || 0;
    const maxAllowedHours = estimatedHours * 1.5;
    
    if (allocatedHours > maxAllowedHours) {
      toast.error(`Allocated hours (${allocatedHours}) cannot exceed 150% of estimated hours (${maxAllowedHours})`);
      return;
    }
    
    // Validate scope of work weightage
    const totalWeightage = getTotalScopeWeightage();
    if (estimatedHours > 0 && totalWeightage > 0 && Math.abs(totalWeightage - 100) > 0.01) {
      toast.error(`Scope of work total weightage (${totalWeightage.toFixed(2)}%) must equal 100%`);
      return;
    }
    
    // If multi-branch project and not in edit mode, show branch allocation dialog
    if (!editMode && formData.branch.length > 1) {
      // Initialize branch allocations with empty values
      const initialAllocations = {};
      formData.branch.forEach(branch => {
        initialAllocations[branch] = {
          allocated_hours: 0,
          allocated_cost: 0,
          allocated_schedule_weeks: 0,
          allocated_schedule_days: 0,
          status: "yet_to_start",
          start_date: formData.start_date,
          end_date: ""
        };
      });
      setBranchAllocations(initialAllocations);
      setAllocationErrors({});
      setBranchAllocationDialogOpen(true);
      return;
    }
    
    // For single branch or edit mode, proceed with normal submission
    await submitProject();
  };

  const submitProject = async () => {
    try {
      const estimatedHours = parseFloat(formData.estimated_hours) || 0;
      const allocatedHours = parseFloat(formData.allocated_hours) || 0;
      
      const payload = {
        ...formData,
        estimated_hours: estimatedHours,
        allocated_hours: allocatedHours,
        base_price: parseFloat(formData.base_price) || 0,
        detailing_cost: parseFloat(formData.detailing_cost) || 0,
        engineering_cost: parseFloat(formData.engineering_cost) || 0,
        schedule_weeks: parseInt(formData.schedule_weeks) || 0,
        schedule_days: parseInt(formData.schedule_days) || 0,
        allocated_schedule_weeks: parseInt(formData.allocated_schedule_weeks) || 0,
        allocated_schedule_days: parseInt(formData.allocated_schedule_days) || 0,
        hours_already_spent: parseInt(formData.hours_already_spent) || 0,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        // Use formData.branch_data for edit mode (from tab), branchAllocations for create mode (from dialog)
        branch_data: formData.branch.length > 1 ? (editMode ? formData.branch_data : branchAllocations) : {}
      };
      
      if (editMode) {
        await api.put(`/projects/${editingProjectId}`, payload);
        toast.success("Project updated successfully!");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created successfully!");
      }
      
      setDialogOpen(false);
      setBranchAllocationDialogOpen(false);
      setEditMode(false);
      setEditingProjectId(null);
      fetchProjects();
      resetForm();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = `Failed to ${editMode ? 'update' : 'create'} project`;
      
      if (errorDetail) {
        if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        } else if (Array.isArray(errorDetail)) {
          // Pydantic validation errors array
          errorMessage = errorDetail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof errorDetail === 'object') {
          errorMessage = JSON.stringify(errorDetail);
        }
      }
      
      toast.error(errorMessage);
      console.error("Project save error:", error.response?.data);
    }
  };

  // Handle branch allocation changes
  const handleBranchAllocationChange = (branch, field, value) => {
    setBranchAllocations(prev => ({
      ...prev,
      [branch]: {
        ...prev[branch],
        [field]: value
      }
    }));
    
    // Auto-calculate end date if schedule fields change
    if (field === 'allocated_schedule_weeks' || field === 'allocated_schedule_days' || field === 'start_date') {
      const startDate = field === 'start_date' ? value : branchAllocations[branch]?.start_date;
      const weeks = field === 'allocated_schedule_weeks' ? value : branchAllocations[branch]?.allocated_schedule_weeks || 0;
      const days = field === 'allocated_schedule_days' ? value : branchAllocations[branch]?.allocated_schedule_days || 0;
      
      if (startDate) {
        const endDate = calculateEndDate(startDate, weeks, days);
        setBranchAllocations(prev => ({
          ...prev,
          [branch]: {
            ...prev[branch],
            end_date: endDate
          }
        }));
      }
    }
    
    // Clear error for this branch
    setAllocationErrors(prev => ({ ...prev, [branch]: null }));
  };

  // Validate branch allocations
  const validateBranchAllocations = () => {
    const errors = {};
    let hasError = false;
    
    const allocatedHours = parseFloat(formData.allocated_hours) || 0;
    const basePrice = parseFloat(formData.base_price) || 0;
    const projectScheduleDays = (parseInt(formData.allocated_schedule_weeks) || 0) * 5 + (parseInt(formData.allocated_schedule_days) || 0);
    
    let totalHours = 0;
    let totalCost = 0;
    
    formData.branch.forEach(branch => {
      const alloc = branchAllocations[branch] || {};
      const branchHours = parseFloat(alloc.allocated_hours) || 0;
      const branchCost = parseFloat(alloc.allocated_cost) || 0;
      const branchScheduleDays = (parseInt(alloc.allocated_schedule_weeks) || 0) * 5 + (parseInt(alloc.allocated_schedule_days) || 0);
      
      totalHours += branchHours;
      totalCost += branchCost;
      
      // Check if branch schedule exceeds project schedule
      if (branchScheduleDays > projectScheduleDays) {
        errors[branch] = `Schedule exceeds project schedule (${formData.allocated_schedule_weeks}w ${formData.allocated_schedule_days}d)`;
        hasError = true;
      }
      
      // Check if allocation is zero
      if (branchHours === 0 || branchCost === 0) {
        errors[branch] = "Hours and cost must be greater than 0";
        hasError = true;
      }
    });
    
    // Check total hours
    if (totalHours > allocatedHours) {
      errors.total = `Total hours (${totalHours}h) exceeds project allocated hours (${allocatedHours}h)`;
      hasError = true;
    }
    
    // Check total cost
    if (totalCost > basePrice) {
      errors.total = `Total cost ($${totalCost}) exceeds project base price ($${basePrice})`;
      hasError = true;
    }
    
    setAllocationErrors(errors);
    return !hasError;
  };

  const handleBranchAllocationSubmit = () => {
    if (validateBranchAllocations()) {
      submitProject();
    }
  };

  // Scope of Work Management Functions
  const handleScopeChange = (scopeId, field, value) => {
    const updatedScopes = formData.scope_of_work.map(scope => {
      if (scope.id === scopeId) {
        // Keep empty string if user clears the field
        const weightage = value === "" ? "" : parseFloat(value) || 0;
        const total_weightage = value === "" ? 0 : parseFloat(value) || 0;
        // Weightage and total_weightage are the same now
        return { ...scope, weightage, total_weightage };
      }
      return scope;
    });
    setFormData({ ...formData, scope_of_work: updatedScopes });
  };

  const handleScopeNameChange = (scopeId, name) => {
    const updatedScopes = formData.scope_of_work.map(scope =>
      scope.id === scopeId ? { ...scope, name } : scope
    );
    setFormData({ ...formData, scope_of_work: updatedScopes });
  };

  const addScope = () => {
    const newScope = {
      id: crypto.randomUUID(),
      name: "New Scope",
      weightage: "",
      total_weightage: 0
    };
    setFormData({ ...formData, scope_of_work: [...formData.scope_of_work, newScope] });
  };

  const deleteScope = (scopeId) => {
    if (formData.scope_of_work.length <= 1) {
      toast.error("At least one scope must remain");
      return;
    }
    const updatedScopes = formData.scope_of_work.filter(scope => scope.id !== scopeId);
    setFormData({ ...formData, scope_of_work: updatedScopes });
  };

  const getTotalScopeWeightage = () => {
    return formData.scope_of_work.reduce((sum, scope) => sum + (scope.total_weightage || 0), 0);
  };

  const resetForm = () => {
    setFormData({
      project_number: "",
      client_project_number: "",
      name: "",
      description: "",
      client_id: "",
      estimation_id: "",
      estimation_number: "",
      estimated_hours: "",
      allocated_hours: "",
      base_price: "",
      detailing_cost: "",
      engineering_cost: "",
      currency: "USD",
      schedule_weeks: "",
      schedule_days: "",
      sales: "",
      branch: [],  // Reset to array
      status: "yet_to_start",
      purchase_order_number: "",
      start_date: "",
      end_date: "",
      assigned_users: [],
      scope_of_work: getDefaultScopes(),
      is_old_project: false,
      hours_already_spent: ""
    });
    setBranchAllocations({});
    setAllocationErrors({});
    setViewingProject(null);
  };

  const handleEdit = async (rowProject) => {
    // Fetch fresh full record — list endpoint slims heavy fields (scope_of_work, branch_data, change_orders)
    let project = rowProject;
    try {
      const res = await api.get(`/projects/${rowProject.id}`);
      project = res.data;
    } catch (err) {
      console.error("Failed to fetch full project, falling back to list row", err);
    }
    setEditMode(true);
    setEditingProjectId(project.id);
    setFormData({
      project_number: project.project_number || "",
      client_project_number: project.client_project_number || "",
      name: project.name || "",
      description: project.description || "",
      client_id: project.client_id || "",
      estimation_id: project.estimation_id || "",
      estimation_number: project.estimation_number || "",
      estimated_hours: (project.estimated_hours || "").toString(),
      allocated_hours: (project.allocated_hours || "").toString(),
      base_price: (project.base_price || "").toString(),
      detailing_cost: (project.detailing_cost || 0).toString(),
      engineering_cost: (project.engineering_cost || 0).toString(),
      currency: project.currency || "USD",
      schedule_weeks: (project.schedule_weeks || 0).toString(),
      schedule_days: (project.schedule_days || 0).toString(),
      allocated_schedule_weeks: (project.allocated_schedule_weeks || 0).toString(),
      allocated_schedule_days: (project.allocated_schedule_days || 0).toString(),
      sales: project.sales || "",
      branch: Array.isArray(project.branch) ? project.branch : project.branch ? [project.branch] : [],  // Handle both array and string for backward compatibility
      branch_data: project.branch_data || {},  // Load existing branch allocation data
      status: project.status || "yet_to_start",
      purchase_order_number: project.purchase_order_number || "",
      start_date: project.start_date ? new Date(project.start_date).toISOString().split('T')[0] : "",
      end_date: project.end_date ? new Date(project.end_date).toISOString().split('T')[0] : "",
      assigned_users: project.assigned_users || [],
      scope_of_work: project.scope_of_work || getDefaultScopes(),
      is_old_project: project.is_old_project || false,
      hours_already_spent: (project.hours_already_spent && project.hours_already_spent > 0) ? project.hours_already_spent.toString() : "",
      client_notes_project_start: project.client_notes_project_start || ""
    });
    setViewingProject(project);  // Store the full project for approval status
    setDialogOpen(true);
  };

  // ─── Estimation version sync ──────────────────────────────────────
  const openEstimationSync = async (project) => {
    setSyncDialog({ open: true, loading: true, saving: false, error: null, project, candidate: null });
    try {
      const res = await api.get(`/projects/${project.id}/estimation-sync-candidates`);
      if (!res.data?.has_candidate) {
        setSyncDialog({
          open: true, loading: false, saving: false,
          error: res.data?.reason || "No newer version available",
          project, candidate: null,
        });
        return;
      }
      setSyncDialog({
        open: true, loading: false, saving: false, error: null,
        project, candidate: res.data,
      });
    } catch (err) {
      setSyncDialog({
        open: true, loading: false, saving: false,
        error: err.response?.data?.detail || "Failed to load sync candidates",
        project, candidate: null,
      });
    }
  };

  const confirmEstimationSync = async () => {
    if (!syncDialog.candidate?.candidate_estimation_id) return;
    try {
      setSyncDialog((d) => ({ ...d, saving: true }));
      const res = await api.post(
        `/projects/${syncDialog.project.id}/sync-from-estimation`,
        { candidate_estimation_id: syncDialog.candidate.candidate_estimation_id },
      );
      toast.success(
        `Project synced to estimation v${res.data.estimation_version} — project is now v${res.data.project_version}`,
      );
      setSyncDialog({ open: false, loading: false, saving: false, error: null, project: null, candidate: null });
      // Refresh the projects list so the badge disappears and versions update
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
      setSyncDialog((d) => ({ ...d, saving: false }));
    }
  };

  const handleDeleteProject = async (project) => {
    const confirmMessage = `Are you sure you want to delete project "${project.name}" (${project.project_number})?\n\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success(`Project "${project.name}" deleted successfully`);
      fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error(error.response?.data?.detail || "Failed to delete project");
    }
  };

  // Approve Hours Already Spent
  const handleApproveHoursAlreadySpent = async () => {
    if (!editingProjectId) return;
    
    try {
      await api.post(`/projects/${editingProjectId}/approve-hours-already-spent`);
      toast.success("Hours already spent approved successfully");
      
      // Refresh projects and update viewingProject
      await fetchProjects();
      const updatedProject = projects.find(p => p.id === editingProjectId);
      if (updatedProject) {
        setViewingProject(updatedProject);
      }
    } catch (error) {
      console.error("Error approving hours:", error);
      toast.error(error.response?.data?.detail || "Failed to approve hours");
    }
  };

  // Submittal functions
  // Auto-calculate submittal status based on steps and HOLD
  const calculateSubmittalStatus = (submittal) => {
    // Manual override takes the highest priority — bypasses both HOLD and step-based derivation
    if (submittal.manually_completed) {
      return "Completed";
    }
    // First check HOLD condition
    if (submittal.hold_enabled) {
      if (submittal.hold_reason === "RFI") {
        return "HOLD due to RFI";
      } else if (submittal.hold_reason === "Client") {
        return "HOLD by Client";
      }
    }
    
    // If not on hold, calculate based on steps
    const steps = submittal.steps || [];
    
    // Find relevant steps
    const approvalStep = steps.find(s => s.step_name === "Approval");
    const reapprovalSteps = steps.filter(s => s.step_name.startsWith("Re-approval"));
    const backFromApprovalStep = steps.find(s => s.step_name === "Back from Approval");
    const backFromReapprovalSteps = steps.filter(s => s.step_name.startsWith("Back from Re-approval"));
    const fabricationSteps = steps.filter(s => s.step_name === "Fabrication" || s.step_name.startsWith("Fabrication "));
    
    // Check Approval or Re-approval status
    if (approvalStep) {
      const approvalStatus = calculatePlanningStatus(approvalStep);
      if (approvalStatus === "Yet to start" || approvalStatus === "Planned") {
        return "App-Progress";
      }
    }
    
    // Check Re-approval steps
    for (const reapprovalStep of reapprovalSteps) {
      const reapprovalStatus = calculatePlanningStatus(reapprovalStep);
      if (reapprovalStatus === "Yet to start" || reapprovalStatus === "Planned") {
        return "App-Progress";
      }
    }
    
    // Check ANY Back-from step (initial approval OR any re-approval cycle) that is Waiting.
    // Bug fix (Feb 03 2026): previously only "Back from Approval" was checked, so a
    // submittal waiting on "Back from Re-approval N" fell through to App-Progress.
    if (backFromApprovalStep) {
      const backFromStatus = calculatePlanningStatus(backFromApprovalStep);
      if (backFromStatus === "Waiting") {
        return "BFA waiting";
      }
    }
    for (const bfr of backFromReapprovalSteps) {
      if (calculatePlanningStatus(bfr) === "Waiting") {
        return "BFA waiting";
      }
    }
    
    // Check Fabrication status
    if (fabricationSteps.length > 0) {
      // Find the latest fabrication step (last one in the array)
      const latestFabricationStep = fabricationSteps[fabricationSteps.length - 1];
      const latestFabStatus = calculatePlanningStatus(latestFabricationStep);
      
      // If latest fabrication is marked as "Full", submittal is completed
      if (latestFabricationStep.submission_complete === "Full" && latestFabStatus === "Submitted") {
        return "Completed";
      }
      
      // Check if any fabrication is in progress
      const anyFabricationInProgress = fabricationSteps.some(fabStep => {
        const fabStatus = calculatePlanningStatus(fabStep);
        return fabStatus === "Planned" || fabStatus === "Partially submitted";
      });
      
      if (anyFabricationInProgress) {
        return "Fab-Progress";
      }
    }
    
    // Default
    return "App-Progress";
  };

  // Calculate submittal completion percentage based on status and client billing
  const calculateCompletionPercentage = (submittal, clientApprovalPercent, clientFabricationPercent) => {
    const status = submittal.current_status;
    const steps = submittal.steps || [];
    
    // If completed, always 100%
    if (status === "Completed") {
      return 100;
    }
    
    // Check approval step status
    const approvalStep = steps.find(s => s.step_name === "Approval");
    if (approvalStep) {
      const approvalPlanningStatus = calculatePlanningStatus(approvalStep);
      // If approval is still "Yet to start" or "Planned", completion is 0%
      if (approvalPlanningStatus === "Yet to start" || approvalPlanningStatus === "Planned") {
        return 0;
      }
    }
    
    // For fabrication in progress (not completed), sum all partial percentages
    const fabricationSteps = steps.filter(s => s.step_name === "Fabrication" || s.step_name.startsWith("Fabrication "));
    
    if (fabricationSteps.length > 0) {
      // Sum all partial percentages from all fabrication steps
      let totalPartialPercentage = 0;
      let hasPartialFabrication = false;
      
      for (const fabStep of fabricationSteps) {
        if (fabStep.submission_complete === "Partial" && fabStep.submission_percentage) {
          totalPartialPercentage += fabStep.submission_percentage;
          hasPartialFabrication = true;
        }
      }
      
      // If there are partial fabrications, calculate: approval % + (sum of partials × fabrication %)
      if (hasPartialFabrication) {
        // Example: 90 + ((30 + 50 + 20) × 10/100) = 90 + 10 = 100
        const fabricationContribution = (totalPartialPercentage / 100) * clientFabricationPercent;
        return clientApprovalPercent + fabricationContribution;
      }
    }
    
    // Default: If approval submitted but no fabrication, return approval %
    return clientApprovalPercent;
  };

  // Calculate total weightage for a scope
  const calculateScopeWeightage = (scopeId) => {
    const scopeSubmittals = editedSubmittals.filter(s => s.scope_of_work_id === scopeId);
    return scopeSubmittals.reduce((sum, submittal) => sum + (submittal.weightage || 0), 0);
  };

  // Calculate scope completion percentage
  // Formula: Sum of (Submittal weightage % × Submittal completion %)
  const calculateScopeCompletionPercentage = (scopeId) => {
    const scopeSubmittals = editedSubmittals.filter(s => s.scope_of_work_id === scopeId);
    
    // Sum of (weightage × completion percentage)
    const totalCompletion = scopeSubmittals.reduce((sum, submittal) => {
      const weightage = submittal.weightage || 0;
      const completionPercentage = calculateCompletionPercentage(
        submittal, 
        currentClientBilling.approval, 
        currentClientBilling.fabrication
      );
      // Contribution = (weightage / 100) × completion percentage
      return sum + (weightage / 100) * completionPercentage;
    }, 0);
    
    return totalCompletion;
  };

  // Calculate overall project completion percentage (Detailing)
  // Formula: Sum of (Scope weightage % × Scope completion %)
  const calculateProjectCompletionPercentage = (scopes) => {
    const totalCompletion = scopes.reduce((sum, scope) => {
      const scopeWeightage = scope.weightage || scope.total_weightage || 0;
      const scopeCompletion = calculateScopeCompletionPercentage(scope.id);
      // Contribution = (scope weightage / 100) × scope completion percentage
      return sum + (scopeWeightage / 100) * scopeCompletion;
    }, 0);
    
    return totalCompletion;
  };

  // Auto-calculate planning status based on dates and submission_complete
  const calculatePlanningStatus = (step) => {
    // Safety check for undefined step or step_name
    if (!step || !step.step_name) {
      return "Yet to start";
    }
    
    const stepName = step.step_name;
    
    // For Approval and Re-approval steps
    if (stepName === "Approval" || stepName.startsWith("Re-approval")) {
      if (!step.planned_date && !step.actual_date) {
        return "Yet to start";
      } else if (step.planned_date && !step.actual_date) {
        return "Planned";
      } else if (step.actual_date) {
        return "Submitted";
      }
    }
    
    // For Back from Approval and Back from Re-approval steps
    if (stepName === "Back from Approval" || stepName.startsWith("Back from Re-approval")) {
      if (!step.received_date) {
        return "Waiting";
      } else {
        return "Received";
      }
    }
    
    // For Fabrication steps
    if (stepName === "Fabrication" || stepName.startsWith("Fabrication ")) {
      if (!step.planned_date && !step.actual_date) {
        return "Yet to start";
      } else if (step.planned_date && !step.actual_date) {
        return "Planned";
      } else if (step.actual_date) {
        // Check submission_complete field
        if (step.submission_complete === "Partial") {
          return "Partially submitted";
        } else if (step.submission_complete === "Full") {
          return "Submitted";
        }
        // If actual_date exists but no submission_complete selected yet
        return "Planned";
      }
    }
    
    return step.planning_status || "Yet to start";
  };

  // Helper functions for date validation in submittals
  // 1) Back from approval date must be >= approval actual date
  const getMinDateForBackFromApproval = (submittal, stepIndex) => {
    const steps = submittal.steps || [];
    // Find the corresponding approval step (should be just before this step)
    const approvalStep = steps[stepIndex - 1];
    if (approvalStep && approvalStep.actual_date) {
      return approvalStep.actual_date;
    }
    return null;
  };

  // 2) All fabrication dates must be >= latest back from approval date
  const getMinDateForFabrication = (submittal) => {
    const steps = submittal.steps || [];
    // Find all "Back from Approval" and "Back from Re-approval" steps
    const backFromApprovalSteps = steps.filter(s => 
      s.step_name === "Back from Approval" || s.step_name.startsWith("Back from Re-approval")
    );
    
    // Get the latest received_date from all back from approval steps
    let latestDate = null;
    for (const step of backFromApprovalSteps) {
      if (step.received_date) {
        if (!latestDate || step.received_date > latestDate) {
          latestDate = step.received_date;
        }
      }
    }
    
    return latestDate;
  };

  // 3) Re-approval dates must be >= back from approval date just before
  const getMinDateForReapproval = (submittal, stepIndex) => {
    const steps = submittal.steps || [];
    const currentStep = steps[stepIndex];
    
    // Extract re-approval number from step name (e.g., "Re-approval 1" -> 1)
    const match = currentStep?.step_name?.match(/Re-approval (\d+)/);
    if (!match) return null;
    
    const reapprovalNum = parseInt(match[1]);
    
    // For Re-approval N, we need to find the corresponding "Back from [Re-]approval" step
    // that triggered its creation (could be "Back from Approval" or "Back from Re-approval N-1")
    let backFromStepName;
    if (reapprovalNum === 1) {
      backFromStepName = "Back from Approval";
    } else {
      backFromStepName = `Back from Re-approval ${reapprovalNum - 1}`;
    }
    
    // Find the corresponding back from step
    const backFromStep = steps.find(s => s.step_name === backFromStepName);
    
    // If the back from step has return_status "Partially Approved" and has received_date,
    // then Re-approval dates must be >= received_date
    if (backFromStep && backFromStep.return_status === "Partially Approved" && backFromStep.received_date) {
      return backFromStep.received_date;
    }
    
    return null;
  };

  const fetchSubmittals = async (projectId) => {
    setLoadingSubmittals(true);
    try {
      const response = await api.get(`/projects/${projectId}/submittals`);
      setSubmittals(response.data);
      setEditedSubmittals(JSON.parse(JSON.stringify(response.data))); // Deep copy
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error("Failed to fetch submittals");
      console.error(error);
    } finally {
      setLoadingSubmittals(false);
    }
  };

  const openSubmittalsDialog = async (projectId) => {
    setCurrentProjectId(projectId);
    setCurrentProjectFull(null);
    setSubmittalsDialogOpen(true);

    // Fetch full project record — the projects list slims scope_of_work[]/change_orders[]/branch_data
    try {
      const projectResponse = await api.get(`/projects/${projectId}`);
      const project = projectResponse.data;
      setCurrentProjectFull(project);

      // Fetch client billing information
      if (project.client_id) {
        const clientResponse = await api.get(`/clients/${project.client_id}`);
        const client = clientResponse.data;
        setCurrentClientBilling({
          approval: client.billing_approval || 0,
          fabrication: client.billing_fabrication || 0
        });
      }
    } catch (error) {
      console.error("Failed to fetch full project or client billing info:", error);
      setCurrentClientBilling({ approval: 90, fabrication: 10 });
    }

    await fetchSubmittals(projectId);
  };

  const createDefaultSubmittals = async (projectId) => {
    const defaultSubmittals = ["Anchor bolt", "Embeds", "Structural", "Miscellaneous"];
    const defaultSteps = [
      { step_name: "Approval", planning_status: "Yet to start", planned_date: null, actual_date: null, received_date: null, return_status: null, submission_complete: null, submission_percentage: null },
      { step_name: "Back from Approval", planning_status: "Waiting", planned_date: null, actual_date: null, received_date: null, return_status: null, submission_complete: null, submission_percentage: null },
      { step_name: "Fabrication", planning_status: "Yet to start", planned_date: null, actual_date: null, received_date: null, return_status: null, submission_complete: null, submission_percentage: null }
    ];

    try {
      for (const name of defaultSubmittals) {
        await api.post(`/projects/${projectId}/submittals`, {
          project_id: projectId,
          submittal_name: name,
          weightage: 0,
          current_status: "App-Progress",
          hold_enabled: false,
          hold_reason: null,
          steps: defaultSteps
        });
      }
      await fetchSubmittals(projectId);
      toast.success("Default submittals created");
    } catch (error) {
      toast.error("Failed to create default submittals");
      console.error(error);
    }
  };

  const addNewSubmittal = async (name, scopeId = null) => {
    const defaultSteps = [
      { step_name: "Approval", planning_status: "Yet to start", planned_date: null, actual_date: null, received_date: null, return_status: null, submission_complete: null, submission_percentage: null },
      { step_name: "Back from Approval", planning_status: "Waiting", planned_date: null, actual_date: null, received_date: null, return_status: null, submission_complete: null, submission_percentage: null },
      { step_name: "Fabrication", planning_status: "Yet to start", planned_date: null, actual_date: null, received_date: null, return_status: null, submission_complete: null, submission_percentage: null }
    ];

    try {
      const response = await api.post(`/projects/${currentProjectId}/submittals`, {
        project_id: currentProjectId,
        scope_of_work_id: scopeId,  // Link to scope
        submittal_name: name,
        weightage: 0,
        current_status: "App-Progress",
        hold_enabled: false,
        hold_reason: null,
        steps: defaultSteps
      });
      
      // Add the new submittal to editedSubmittals without fetching (preserves unsaved changes)
      const newSubmittal = response.data;
      setEditedSubmittals([...editedSubmittals, newSubmittal]);
      setSubmittals([...submittals, newSubmittal]);
      
      toast.success("Submittal added");
    } catch (error) {
      toast.error("Failed to add submittal");
      console.error(error);
    }
  };

  // Local update without saving to API
  const updateSubmittalLocal = (submittalId, updateData) => {
    const newEditedSubmittals = editedSubmittals.map(submittal => {
      if (submittal.id === submittalId) {
        const updatedSubmittal = { ...submittal, ...updateData };
        
        // Auto-calculate status if steps, hold, or manual-complete changed
        if (
          updateData.steps ||
          updateData.hold_enabled !== undefined ||
          updateData.hold_reason !== undefined ||
          updateData.manually_completed !== undefined
        ) {
          updatedSubmittal.current_status = calculateSubmittalStatus(updatedSubmittal);
        }
        
        // Auto-calculate completion percentage
        updatedSubmittal.completion_percentage = calculateCompletionPercentage(
          updatedSubmittal, 
          currentClientBilling.approval, 
          currentClientBilling.fabrication
        );
        
        return updatedSubmittal;
      }
      return submittal;
    });
    
    setEditedSubmittals(newEditedSubmittals);
    setHasUnsavedChanges(true);
  };

  // Save all changes to API
  const saveAllSubmittals = async () => {
    try {
      // Update each changed submittal
      for (const editedSubmittal of editedSubmittals) {
        await api.put(`/projects/${currentProjectId}/submittals/${editedSubmittal.id}`, editedSubmittal);
      }
      await fetchSubmittals(currentProjectId);
      toast.success("All changes saved successfully");
    } catch (error) {
      toast.error("Failed to save changes");
      console.error(error);
    }
  };

  const updateSubmittal = async (submittalId, updateData) => {
    try {
      // Auto-calculate status if steps changed
      if (updateData.steps) {
        const submittal = submittals.find(s => s.id === submittalId);
        if (submittal) {
          const updatedSubmittal = { ...submittal, ...updateData };
          updateData.current_status = calculateSubmittalStatus(updatedSubmittal);
        }
      }
      
      await api.put(`/projects/${currentProjectId}/submittals/${submittalId}`, updateData);
      await fetchSubmittals(currentProjectId);
      toast.success("Submittal updated");
    } catch (error) {
      toast.error("Failed to update submittal");
      console.error(error);
    }
  };

  const deleteSubmittal = async (submittalId) => {
    if (!confirm("Are you sure you want to delete this submittal?")) return;
    
    try {
      await api.delete(`/projects/${currentProjectId}/submittals/${submittalId}`);
      
      // Remove from local state instead of refetching to preserve unsaved changes in other submittals
      setSubmittals(prev => prev.filter(s => s.id !== submittalId));
      setEditedSubmittals(prev => prev.filter(s => s.id !== submittalId));
      
      toast.success("Submittal deleted");
    } catch (error) {
      toast.error("Failed to delete submittal");
      console.error(error);
    }
  };

  // Note: Manual addReapprovalStep removed - steps are now created automatically based on return status

  const handleReturnStatusChange = (submittal, stepIndex, returnStatus) => {
    let newSteps = [...submittal.steps];
    newSteps[stepIndex].return_status = returnStatus;
    
    const currentStep = newSteps[stepIndex];
    
    // Determine which re-approval number this corresponds to
    let reapprovalNum;
    if (currentStep.step_name === "Back from Approval") {
      reapprovalNum = 1;
    } else if (currentStep.step_name && currentStep.step_name.startsWith("Back from Re-approval")) {
      const match = currentStep.step_name.match(/\d+/);
      if (match) {
        const currentNum = parseInt(match[0]);
        reapprovalNum = currentNum + 1;
      }
    }
    
    // FIRST: ALWAYS clean up all subsequent steps >= reapprovalNum
    // This happens REGARDLESS of what the new return status is
    // This ensures when changing return status (including clearing it), old steps are removed
    if (reapprovalNum) {
      const stepsToRemove = [];
      newSteps.forEach((step) => {
        // Remove Fabrication N (where N >= reapprovalNum)
        if (step.step_name && step.step_name.match(/^Fabrication \d+$/)) {
          const match = step.step_name.match(/\d+/);
          if (match) {
            const num = parseInt(match[0]);
            if (num >= reapprovalNum) {
              stepsToRemove.push(step.step_name);
            }
          }
        }
        // Remove Re-approval N (where N >= reapprovalNum)
        else if (step.step_name && step.step_name.startsWith("Re-approval ")) {
          const match = step.step_name.match(/\d+/);
          if (match) {
            const num = parseInt(match[0]);
            if (num >= reapprovalNum) {
              stepsToRemove.push(step.step_name);
            }
          }
        }
        // Remove Back from Re-approval N (where N >= reapprovalNum)
        else if (step.step_name && step.step_name.startsWith("Back from Re-approval ")) {
          const match = step.step_name.match(/\d+/);
          if (match) {
            const num = parseInt(match[0]);
            if (num >= reapprovalNum) {
              stepsToRemove.push(step.step_name);
            }
          }
        }
      });
      
      if (stepsToRemove.length > 0) {
        newSteps = newSteps.filter(s => !stepsToRemove.includes(s.step_name));
        console.log(`Cleaned up ${stepsToRemove.length} steps:`, stepsToRemove);
      }
    }
    
    // THEN: Create new steps based on the new return status
    // If status is "Partially Approved", create Fabrication N (Partial) + Re-approval cycle
    if (returnStatus === "Partially Approved") {
      // Steps already cleaned up above, now create new ones
      if (reapprovalNum) {
        // Store the received_date from Back from [Re-]approval for validation
        // Re-approval dates must be same or after this date
        const backFromApprovalReceivedDate = currentStep.received_date;
        
        // Create Fabrication N (forced Partial) - ALL fields reset to null/default
        const fabricationNStep = {
          step_name: `Fabrication ${reapprovalNum}`,
          planning_status: "Yet to start",
          planned_date: null,
          actual_date: null,
          received_date: null,
          return_status: null,
          submission_complete: "Partial",  // Forced to Partial
          submission_percentage: null,  // Must be filled by user - reset to null
          created_by_partial_approval: true  // Flag to indicate this was created by Partially Approved
        };
        
        // Create new re-approval step - dates are NULL by default
        // User must select dates, which will be validated to be >= backFromApprovalReceivedDate
        const reapprovalStep = {
          step_name: `Re-approval ${reapprovalNum}`,
          planning_status: "Yet to start",
          planned_date: null,  // NULL by default - user must select
          actual_date: null,   // NULL by default - user must select
          received_date: null,
          return_status: null,
          submission_complete: null,
          submission_percentage: null
        };
        
        // Create back from re-approval step - ALL fields reset to null/default
        const backFromReapprovalStep = {
          step_name: `Back from Re-approval ${reapprovalNum}`,
          planning_status: "Waiting",
          planned_date: null,  // Reset to null
          actual_date: null,  // Reset to null
          received_date: null,  // Reset to null
          return_status: null,  // Reset to null
          submission_complete: null,  // Reset to null
          submission_percentage: null  // Reset to null
        };
        
        // Insert before main Fabrication step
        const fabricationIndex = newSteps.findIndex(s => s.step_name === "Fabrication");
        if (fabricationIndex !== -1) {
          newSteps.splice(fabricationIndex, 0, fabricationNStep, reapprovalStep, backFromReapprovalStep);
        } else {
          newSteps.push(fabricationNStep, reapprovalStep, backFromReapprovalStep);
        }
        
        console.log("Created Re-approval step:", reapprovalStep);
        toast.success(`Fabrication ${reapprovalNum} (Partial) and Re-approval ${reapprovalNum} automatically created`);
      }
    }
    // If status is "Revised and Resubmit" or "Rejected", create re-approval steps
    else if (returnStatus === "Revised and Resubmit" || returnStatus === "Rejected") {
      // Steps already cleaned up above, now create new ones
      if (reapprovalNum) {
        // Create new re-approval step - ALL fields reset to null/default
        const reapprovalStep = {
          step_name: `Re-approval ${reapprovalNum}`,
          planning_status: "Yet to start",
          planned_date: null,  // Reset to null
          actual_date: null,  // Reset to null
          received_date: null,  // Reset to null
          return_status: null,  // Reset to null
          submission_complete: null,  // Reset to null
          submission_percentage: null  // Reset to null
        };
        
        // Create back from re-approval step - ALL fields reset to null/default
        const backFromReapprovalStep = {
          step_name: `Back from Re-approval ${reapprovalNum}`,
          planning_status: "Waiting",
          planned_date: null,  // Reset to null
          actual_date: null,  // Reset to null
          received_date: null,  // Reset to null
          return_status: null,  // Reset to null
          submission_complete: null,  // Reset to null
          submission_percentage: null  // Reset to null
        };
        
        // Insert before Fabrication
        const fabricationIndex = newSteps.findIndex(s => s.step_name === "Fabrication");
        if (fabricationIndex !== -1) {
          newSteps.splice(fabricationIndex, 0, reapprovalStep, backFromReapprovalStep);
        } else {
          newSteps.push(reapprovalStep, backFromReapprovalStep);
        }
        
        toast.success(`Re-approval ${reapprovalNum} automatically created`);
      }
    }
    // If status is "Approved" or "Approved as noted", cleanup already done above
    // No new steps to create - workflow proceeds to main Fabrication
    else if (returnStatus === "Approved" || returnStatus === "Approved as noted") {
      if (reapprovalNum) {
        toast.info(`Submission approved - workflow proceeds to Fabrication`);
      }
    }
    // If return status is cleared/empty or any other status, cleanup has already happened
    // No new steps to create
    else if (!returnStatus || returnStatus === "") {
      console.log("Return status cleared - steps cleaned up");
    }
    
    // Always update the submittal with the new steps (cleaned up and/or with new steps added)
    updateSubmittalLocal(submittal.id, { steps: newSteps });
  };

  // Note: Removed manual delete function - steps are now managed automatically based on return status

  const handleSubmissionPercentageChange = (submittal, stepIndex, value) => {
    const newSteps = [...submittal.steps];
    // Parse and validate percentage (0-100)
    const percentage = parseFloat(value);
    if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
      newSteps[stepIndex].submission_percentage = percentage;
    } else if (value === '' || value === null) {
      newSteps[stepIndex].submission_percentage = null;
    }
    updateSubmittalLocal(submittal.id, { steps: newSteps });
  };

  const handleSubmissionCompleteChange = (submittal, stepIndex, value) => {
    const newSteps = [...submittal.steps];
    newSteps[stepIndex].submission_complete = value;
    
    // If "Partial" is selected, automatically add next Fabrication step
    if (value === "Partial") {
      const currentStep = newSteps[stepIndex];
      
      // Initialize submission_percentage if not set
      if (!newSteps[stepIndex].submission_percentage) {
        newSteps[stepIndex].submission_percentage = null;
      }
      
      // Count existing Fabrication steps to determine next number
      const fabricationSteps = newSteps.filter(s => 
        s.step_name === "Fabrication" || s.step_name.startsWith("Fabrication ")
      );
      const nextFabricationNum = fabricationSteps.length + 1;
      
      // Create new Fabrication step
      const newFabricationStep = {
        step_name: nextFabricationNum === 2 ? "Fabrication 2" : `Fabrication ${nextFabricationNum}`,
        planning_status: "Yet to start",
        planned_date: null,
        actual_date: null,
        received_date: null,
        return_status: null,
        submission_complete: null,
        submission_percentage: null
      };
      
      // Add at the end
      newSteps.push(newFabricationStep);
      
      toast.success(`Fabrication ${nextFabricationNum} automatically created`);
    }
    
    // If "Full" is selected, remove all subsequent Fabrication steps
    if (value === "Full") {
      const currentStepName = newSteps[stepIndex].step_name;
      
      // Find all fabrication steps after the current one
      const stepsToKeep = [];
      let foundCurrent = false;
      
      for (let i = 0; i < newSteps.length; i++) {
        const step = newSteps[i];
        const isFabricationStep = step.step_name === "Fabrication" || step.step_name.startsWith("Fabrication ");
        
        if (i === stepIndex) {
          foundCurrent = true;
          stepsToKeep.push(step);
        } else if (foundCurrent && isFabricationStep) {
          // Skip this fabrication step (remove it)
          continue;
        } else {
          stepsToKeep.push(step);
        }
      }
      
      if (stepsToKeep.length < newSteps.length) {
        toast.success("Subsequent fabrication steps removed");
        updateSubmittalLocal(submittal.id, { steps: stepsToKeep });
        return;
      }
    }
    
    updateSubmittalLocal(submittal.id, { steps: newSteps });
  };


  const getStatusColor = (status) => {
    switch (status) {
      case "yet_to_start":
        return "bg-gray-500";
      case "in_progress":
        return "bg-blue-500";
      case "waiting_for_approval_returns":
        return "bg-yellow-500";
      case "hold_for_rfi":
        return "bg-orange-500";
      case "hold_by_client":
        return "bg-red-500";
      case "completed":
        return "bg-green-500";
      case "cancelled":
        return "bg-gray-700";
      case "active":
        return "bg-green-500";
      case "on_hold":
        return "bg-orange-500";
      default:
        return "bg-gray-500";
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return "Unknown Client";
    const number = client.client_number || "N/A";
    const name = client.client_name || client.company || "Unknown";
    return `${number} - ${name}`;
  };

  const getClientDisplay = (client) => {
    const number = client.client_number || "N/A";
    const name = client.client_name || client.company || "Unknown";
    return `${number} - ${name}`;
  };

  const getProjectDisplay = (project) => {
    return `${project.project_number || project.id.substring(0, 8)} - ${project.name}`;
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name || ''}`.trim() : "";
  };

  const formatStatus = (status) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getTotalProjectValue = (project) => {
    // For CAD projects, use USD equivalent; for USD projects, use base_price
    const basePrice = project.currency === "CAD" && project.usd_base_price 
      ? parseFloat(project.usd_base_price) 
      : parseFloat(project.base_price) || 0;
    
    // Prefer server-computed summary; list endpoint slims change_orders array
    let approvedChanges = 0;
    if (project.approved_change_orders_value !== undefined && project.approved_change_orders_value !== null) {
      approvedChanges = project.currency === "CAD" && project.approved_change_orders_usd_value !== null && project.approved_change_orders_usd_value !== undefined
        ? project.approved_change_orders_usd_value
        : project.approved_change_orders_value;
    } else if (project.change_orders) {
      // Fallback: compute from full array (detail endpoint still returns it)
      approvedChanges = project.change_orders
        .filter(co => co.status === "approved")
        .reduce((sum, co) => {
          const coValue = project.currency === "CAD" && co.usd_cost_impact
            ? parseFloat(co.usd_cost_impact)
            : parseFloat(co.cost_impact) || 0;
          return sum + coValue;
        }, 0);
    }
    return basePrice + approvedChanges;
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await api.get("/export/projects", {
        responseType: 'blob'
      });
      
      // Check if response is actually a blob
      if (!(response.data instanceof Blob)) {
        throw new Error("Invalid response type");
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `projects_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url); // Clean up
      toast.success("Projects log downloaded successfully!");
    } catch (error) {
      console.error("Excel download error:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to download projects log";
      toast.error(errorMessage);
    }
  };

  const handleApplyImport = async (selectedNew, selectedUpdates) => {
    try {
      const payload = {
        selected_new: selectedNew,
        selected_updates: selectedUpdates,
        new_records: importPreviewData?.new_records || [],
        updates: importPreviewData?.updates || []
      };
      
      const response = await api.post("/import/projects/apply", payload);
      
      toast.success(`Successfully imported ${response.data.imported} and updated ${response.data.updated} projects!`);
      
      if (response.data.errors.length > 0) {
        console.log("Import errors:", response.data.errors);
        toast.warning(`Import completed with ${response.data.errors.length} errors. Check console for details.`);
      }
      
      setImportPreviewOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error("Failed to apply import");
    }
  };


  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="projects-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small text-md3-on-surface mb-2">Projects</h1>
            <p className="text-md3-on-surface-variant">Manage your project portfolio</p>
          </div>
          <div className="flex gap-2">
            {/* Download Excel - requires export permission and not Projects dept user */}
            {canExport() && !isProjectsDeptUser() && (
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={handleDownloadExcel} data-testid="download-projects-button">
                <Download className="mr-2 h-4 w-4" /> Download Excel
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditMode(false);
                setEditingProjectId(null);
                resetForm();
              } else {
                // Fetch available estimations when opening for new project
                if (!editMode) {
                  fetchAvailableEstimations();
                }
              }
            }}>
            {/* New Project button - requires create permission and not Projects dept user */}
            {canCreate() && !isProjectsDeptUser() && (
              <DialogTrigger asChild>
                <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" data-testid="create-project-button">
                  <Plus className="mr-2 h-4 w-4" /> New Project
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? "Edit Project" : "Create New Project"}</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-gray-600">
                  {editMode ? "Update project details" : "Add a new project with estimation and pricing"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <Tabs defaultValue="details" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="details">Project Details</TabsTrigger>
                    <TabsTrigger value="branches" disabled={!formData.branch || formData.branch.length <= 1}>
                      Branch Allocation {formData.branch && formData.branch.length > 1 && `(${formData.branch.length})`}
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="details" className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  {!editMode && (
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="estimation_id" className="text-md3-label-large text-gray-700 font-medium">Estimation *</Label>
                      {(() => {
                        // Sort descending by estimation_number (newest first) so users
                        // see the most recently awarded estimations at the top of the list.
                        // Sort uses numeric-aware comparison so EST-200 > EST-99.
                        const sortedEstimations = [...estimations].sort((a, b) => {
                          const an = a.estimation_number || "";
                          const bn = b.estimation_number || "";
                          return bn.localeCompare(an, undefined, { numeric: true, sensitivity: "base" });
                        });
                        const selected = sortedEstimations.find(e => e.id === formData.estimation_id);
                        return (
                          <Popover open={estimationPickerOpen} onOpenChange={setEstimationPickerOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={estimationPickerOpen}
                                className="w-full justify-between font-normal"
                                data-testid="project-estimation-select"
                              >
                                <span className={selected ? "" : "text-md3-on-surface-variant"}>
                                  {selected
                                    ? `${selected.estimation_number}${selected.version ? ` v${selected.version}` : ""} - ${selected.project_name}`
                                    : "Select awarded estimation"}
                                </span>
                                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                              <Command
                                filter={(value, search) => {
                                  // CommandItem's `value` is "${number}|${name}" (lowercased)
                                  if (!search) return 1;
                                  return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                                }}
                              >
                                <CommandInput
                                  placeholder="Search by number or project name…"
                                  data-testid="project-estimation-search"
                                />
                                <CommandList>
                                  <CommandEmpty>No estimation matches your search.</CommandEmpty>
                                  <CommandGroup>
                                    {sortedEstimations.map((estimation) => {
                                      const numStr = estimation.estimation_number || "";
                                      const nameStr = estimation.project_name || "";
                                      const verStr = estimation.version || "";
                                      return (
                                        <CommandItem
                                          key={estimation.id}
                                          value={`${numStr}|${verStr}|${nameStr}`}
                                          onSelect={() => {
                                            handleEstimationSelect(estimation.id);
                                            setEstimationPickerOpen(false);
                                          }}
                                          data-testid={`project-estimation-option-${estimation.id}`}
                                        >
                                          <Check className={`mr-2 h-4 w-4 ${formData.estimation_id === estimation.id ? "opacity-100" : "opacity-0"}`} />
                                          <span className="font-mono text-md3-body-small">{numStr}</span>
                                          {verStr && (
                                            <span className="ml-1.5 text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md3-sm px-1.5 py-0.5">
                                              v{verStr}
                                            </span>
                                          )}
                                          <span className="mx-2 text-md3-on-surface-variant">·</span>
                                          <span className="truncate">{nameStr}</span>
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                      <p className="text-xs text-md3-on-surface-variant">All fields will auto-populate.</p>
                      {estimations.length === 0 && (
                        <p className="text-xs text-red-500">No available estimations. Please award an estimation or all estimations are already used in projects.</p>
                      )}
                    </div>
                  )}
                  {editMode && formData.estimation_number && (
                    <div className="space-y-2 col-span-2">
                      <Label>Estimation</Label>
                      <Input className="bg-gray-100 bg-white border-gray-300 cursor-not-allowed focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" value={formData.estimation_number}
                        disabled
                        
                      />
                      <p className="text-xs text-md3-on-surface-variant">Estimation cannot be changed after project creation.</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="project_number" className="text-md3-label-large text-gray-700 font-medium">Project Number</Label>
                    <Input id="project_number"
                      data-testid="project-number-input"
                      placeholder={`e.g., ${getNextProjectNumber(getLastProjectNumber(projects)) || "26-001"}`}
                      value={formData.project_number}
                      onChange={(e) => setFormData({ ...formData, project_number: e.target.value })}
                      disabled={editMode}
                      className={editMode ? "bg-gray-100 cursor-not-allowed" : `bg-white focus:ring-2 ${isDuplicateProjectNumber(projects, formData.project_number) ? "border-red-500 focus:border-red-500 focus:ring-red-200" : "border-gray-300 focus:border-md3-primary focus:ring-md3-primary/20"}`}
                    />
                    {!editMode && isDuplicateProjectNumber(projects, formData.project_number) && (
                      <p className="text-xs text-red-600 font-medium" data-testid="project-number-duplicate-warning">
                        Project number {formData.project_number.trim()} already exists. Please use a different number.
                      </p>
                    )}
                    {editMode ? (
                      <p className="text-xs text-md3-on-surface-variant">Project number cannot be changed</p>
                    ) : getLastProjectNumber(projects) && (
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded" data-testid="last-project-number">
                        <p className="text-xs text-blue-700">
                          <strong>Last Project Number:</strong> {getLastProjectNumber(projects)}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_project_number" className="text-md3-label-large text-gray-700 font-medium">Client Project Number</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="client_project_number"
                      placeholder="Enter client's project number"
                      value={formData.client_project_number}
                      onChange={(e) => setFormData({ ...formData, client_project_number: e.target.value })}
                    />
                    <p className="text-xs text-md3-on-surface-variant">Client's reference number for this project</p>
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="name" className="text-md3-label-large text-gray-700 font-medium">Project Name *</Label>
                    <Input id="name"
                      data-testid="project-name-input"
                      placeholder="Enter project name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={formData.estimation_id !== ""}
                      className={formData.estimation_id !== "" ? "bg-gray-100 cursor-not-allowed" : ""}
                      required
                    />
                    {formData.estimation_id !== "" && (
                      <p className="text-xs text-md3-on-surface-variant">Auto-populated from estimation (not editable)</p>
                    )}
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label htmlFor="description" className="text-md3-label-large text-gray-700 font-medium">Description</Label>
                    <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="description"
                      data-testid="project-description-input"
                      placeholder="Project description (optional)"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="client_id" className="text-md3-label-large text-gray-700 font-medium">Client *</Label>
                    {formData.estimation_id !== "" ? (
                      <>
                        <Input value={clients.find(c => c.id === formData.client_id)?.client_name || clients.find(c => c.id === formData.client_id)?.company || ""}
                          disabled
                          className="bg-gray-100 cursor-not-allowed"
                        />
                        <p className="text-xs text-md3-on-surface-variant">Auto-populated from estimation (not editable)</p>
                      </>
                    ) : (
                      <>
                        <Select value={formData.client_id} onValueChange={(value) => setFormData({ ...formData, client_id: value })}>
                          <SelectTrigger data-testid="project-client-select">
                            <SelectValue placeholder="Select client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {getClientDisplay(client)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {clients.length === 0 && (
                          <p className="text-xs text-md3-on-surface-variant">No clients found. <a href="/clients" className="text-blue-600 hover:underline">Add a client first</a></p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Client Contacts - Project and Billing */}
                  {formData.client_id && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="project_contact_id" className="text-md3-label-large text-gray-700 font-medium">Project Contact</Label>
                        <Select 
                          value={formData.project_contact_id || "none"} 
                          onValueChange={(value) => setFormData({ ...formData, project_contact_id: value === "none" ? "" : value })}
                        >
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select project contact" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            {pickableContacts(contacts, "Project", formData.project_contact_id).map((contact) => (
                              <SelectItem key={contact.id} value={contact.id} disabled={contact.is_left}>
                                {contactLabel(contact)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {pickableContacts(contacts, "Project", formData.project_contact_id).length === 0 && (
                          <p className="text-xs text-md3-on-surface-variant">No contacts with "Project" role found for this client</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="billing_contact_id" className="text-md3-label-large text-gray-700 font-medium">Billing Contact</Label>
                        <Select 
                          value={formData.billing_contact_id || "none"} 
                          onValueChange={(value) => setFormData({ ...formData, billing_contact_id: value === "none" ? "" : value })}
                        >
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue placeholder="Select billing contact" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">-</SelectItem>
                            {pickableContacts(contacts, "Billing", formData.billing_contact_id).map((contact) => (
                              <SelectItem key={contact.id} value={contact.id} disabled={contact.is_left}>
                                {contactLabel(contact)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {pickableContacts(contacts, "Billing", formData.billing_contact_id).length === 0 && (
                          <p className="text-xs text-md3-on-surface-variant">No contacts with "Billing" role found for this client</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Hours - Estimated and Allocated on same line */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="estimated_hours" className="text-md3-label-large text-gray-700 font-medium">Estimated Hours *</Label>
                      <Input id="estimated_hours"
                        data-testid="project-hours-input"
                        type="number"
                        step="0.5"
                        placeholder="Hours"
                        value={formData.estimated_hours}
                        onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
                        disabled={formData.estimation_id !== ""}
                        className={formData.estimation_id !== "" ? "bg-gray-100 cursor-not-allowed" : ""}
                        required
                      />
                      {formData.estimation_id !== "" && (
                        <p className="text-xs text-md3-on-surface-variant">Auto-populated from estimation</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="allocated_hours" className="text-md3-label-large text-gray-700 font-medium">Allocated Hours *</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="allocated_hours"
                        type="number"
                        step="0.5"
                        placeholder="Hours"
                        value={formData.allocated_hours}
                        onChange={(e) => setFormData({ ...formData, allocated_hours: e.target.value })}
                        required
                      />
                      {formData.estimated_hours && formData.allocated_hours && parseFloat(formData.allocated_hours) > parseFloat(formData.estimated_hours) && (
                        <p className="text-xs text-orange-600 font-medium">
                          +{(parseFloat(formData.allocated_hours) - parseFloat(formData.estimated_hours)).toFixed(2)}h extra
                        </p>
                      )}
                      {(!formData.estimated_hours || !formData.allocated_hours || parseFloat(formData.allocated_hours) <= parseFloat(formData.estimated_hours)) && (
                        <p className="text-xs text-md3-on-surface-variant">For timesheet approval</p>
                      )}
                    </div>
                  </div>

                  {/* Cost Breakdown from Estimation - Only visible to Management, Sales, and Accounts */}
                  {canViewCosts() && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="detailing_cost" className="text-md3-label-large text-gray-700 font-medium">Detailing Cost ({formData.currency})</Label>
                        <Input id="detailing_cost"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={formData.detailing_cost}
                          onChange={(e) => {
                            const detailing = parseFloat(e.target.value) || 0;
                            const engineering = parseFloat(formData.engineering_cost) || 0;
                            setFormData({ 
                              ...formData, 
                              detailing_cost: e.target.value,
                              base_price: (detailing + engineering).toString()
                            });
                          }}
                          disabled={formData.estimation_id !== ""}
                          className={formData.estimation_id !== "" ? "bg-gray-100 cursor-not-allowed" : ""}
                        />
                        {formData.estimation_id !== "" && (
                          <p className="text-xs text-md3-on-surface-variant">Auto-populated from estimation (not editable)</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="engineering_cost" className="text-md3-label-large text-gray-700 font-medium">Engineering Cost ({formData.currency})</Label>
                        <Input id="engineering_cost"
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={formData.engineering_cost}
                          onChange={(e) => {
                            const engineering = parseFloat(e.target.value) || 0;
                            const detailing = parseFloat(formData.detailing_cost) || 0;
                            setFormData({ 
                              ...formData, 
                              engineering_cost: e.target.value,
                              base_price: (detailing + engineering).toString()
                            });
                          }}
                          disabled={formData.estimation_id !== ""}
                          className={formData.estimation_id !== "" ? "bg-gray-100 cursor-not-allowed" : ""}
                        />
                        {formData.estimation_id !== "" && (
                          <p className="text-xs text-md3-on-surface-variant">Auto-populated from estimation (not editable)</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="base_price" className="text-md3-label-large text-gray-700 font-medium">Base Price ({formData.currency}) *</Label>
                        <Input className="bg-gray-100 bg-white border-gray-300 cursor-not-allowed focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" id="base_price"
                          data-testid="project-price-input"
                          type="number"
                          step="0.01"
                          placeholder="Auto-calculated"
                          value={formData.base_price}
                          disabled
                          
                        />
                        <p className="text-xs text-md3-on-surface-variant">Auto-calculated: Detailing + Engineering Cost</p>
                      </div>
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="currency" className="text-md3-label-large text-gray-700 font-medium">Currency</Label>
                    {formData.estimation_id !== "" ? (
                      <>
                        <Input className="bg-gray-100 bg-white border-gray-300 cursor-not-allowed focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" value={formData.currency}
                          disabled
                          
                        />
                        <p className="text-xs text-md3-on-surface-variant">Auto-populated from estimation (not editable)</p>
                      </>
                    ) : (
                      <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">US Dollar (USD)</SelectItem>
                          <SelectItem value="CAD">Canadian Dollar (CAD)</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Estimated Schedule - Weeks and Days on same line */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="schedule_weeks" className="text-md3-label-large text-gray-700 font-medium">Estimated Schedule (Weeks)</Label>
                      <Input id="schedule_weeks"
                        type="number"
                        placeholder="0"
                        value={formData.schedule_weeks}
                        onChange={(e) => setFormData({ ...formData, schedule_weeks: e.target.value })}
                        disabled={formData.estimation_id !== ""}
                        className={formData.estimation_id !== "" ? "bg-gray-100 cursor-not-allowed" : ""}
                      />
                      {formData.estimation_id !== "" && (
                        <p className="text-xs text-md3-on-surface-variant">From estimation</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="schedule_days" className="text-md3-label-large text-gray-700 font-medium">Estimated Schedule (Days)</Label>
                      <Input id="schedule_days"
                        type="number"
                        placeholder="0"
                        value={formData.schedule_days}
                        onChange={(e) => setFormData({ ...formData, schedule_days: e.target.value })}
                        disabled={formData.estimation_id !== ""}
                        className={formData.estimation_id !== "" ? "bg-gray-100 cursor-not-allowed" : ""}
                      />
                      {formData.estimation_id !== "" && (
                        <p className="text-xs text-md3-on-surface-variant">Max 4 days</p>
                      )}
                    </div>
                  </div>

                  {/* Allocated Schedule - Weeks and Days on same line */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="allocated_schedule_weeks" className="text-md3-label-large text-gray-700 font-medium">Allocated Schedule (Weeks)</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="allocated_schedule_weeks"
                        type="number"
                        placeholder="0"
                        value={formData.allocated_schedule_weeks}
                        onChange={(e) => setFormData({ ...formData, allocated_schedule_weeks: e.target.value })}
                      />
                      <p className="text-xs text-md3-on-surface-variant">Editable schedule</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="allocated_schedule_days" className="text-md3-label-large text-gray-700 font-medium">Allocated Schedule (Days)</Label>
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="allocated_schedule_days"
                        type="number"
                        placeholder="0"
                        max="4"
                        value={formData.allocated_schedule_days}
                        onChange={(e) => setFormData({ ...formData, allocated_schedule_days: e.target.value })}
                      />
                      <p className="text-xs text-md3-on-surface-variant">Max 4 days</p>
                    </div>
                  </div>

                  {/* Sales from Estimation */}
                  <div className="space-y-2">
                    <Label htmlFor="sales" className="text-md3-label-large text-gray-700 font-medium">Sales Person</Label>
                    {formData.estimation_id !== "" ? (
                      <>
                        <Input value={users.find(u => u.id === formData.sales) ? `${users.find(u => u.id === formData.sales).first_name} ${users.find(u => u.id === formData.sales).last_name || ''}`.trim() : "None"}
                          disabled
                          className="bg-gray-100 cursor-not-allowed"
                        />
                        <p className="text-xs text-md3-on-surface-variant">Auto-populated from estimation (not editable)</p>
                      </>
                    ) : (
                      <Select value={formData.sales || "none"} onValueChange={(value) => setFormData({ ...formData, sales: value === "none" ? "" : value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue placeholder="Select sales person" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {users.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.first_name} {user.last_name || ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Branch - Multiple Selection */}
                  <div className="space-y-2">
                    <Label>Branch * (Select at least one)</Label>
                    <div className="flex flex-col space-y-2 border rounded-md3-md p-3 bg-gray-50">
                      {[...branches.map(b => ({ name: b.name, label: `${b.name} (${b.country})` })), { name: "Vendor", label: "Vendor" }].map((branchOption) => (
                        <div key={branchOption.name} className="flex items-center space-x-2">
                          <Checkbox
                            id={`branch-${branchOption.name}`}
                            checked={formData.branch.includes(branchOption.name)}
                            onCheckedChange={async (checked) => {
                              if (checked) {
                                // Add branch
                                const newBranches = [...formData.branch, branchOption.name];
                                const newBranchData = { ...formData.branch_data };
                                
                                // If converting from single to multi-branch, initialize with project values
                                if (formData.branch.length === 1 && !formData.branch_data[formData.branch[0]]) {
                                  const originalBranch = formData.branch[0];
                                  let allocatedHours = parseFloat(formData.allocated_hours) || 0;
                                  
                                  // Check earned hours from timesheets for the original branch
                                  if (editMode && editingProjectId) {
                                    try {
                                      const response = await api.get(`/projects/${editingProjectId}/hours-summary`);
                                      const hoursSummary = response.data;
                                      
                                      // Get earned hours for the original branch
                                      const earnedHours = hoursSummary.earned_by_branch?.[originalBranch] || 0;
                                      
                                      // Ensure allocated hours is at least the earned hours
                                      if (earnedHours > allocatedHours) {
                                        allocatedHours = earnedHours;
                                        toast.info(`Original branch allocated hours adjusted to ${earnedHours.toFixed(2)} (minimum earned hours)`);
                                      }
                                    } catch (error) {
                                      console.error("Failed to fetch hours summary:", error);
                                      // Continue with original allocated hours if fetch fails
                                    }
                                  }
                                  
                                  // Pre-fill original branch with project values
                                  newBranchData[originalBranch] = {
                                    allocated_hours: allocatedHours,
                                    allocated_cost: parseFloat(formData.base_price) || 0,
                                    allocated_schedule_weeks: parseInt(formData.schedule_weeks) || 0,
                                    allocated_schedule_days: parseInt(formData.schedule_days) || 0,
                                    status: formData.status || "In Progress"
                                  };
                                }
                                
                                // Initialize new branch with empty values
                                newBranchData[branchOption.name] = {
                                  allocated_hours: null,
                                  allocated_cost: null,
                                  allocated_schedule_weeks: null,
                                  allocated_schedule_days: null,
                                  status: "Not Started"
                                };
                                
                                setFormData({ ...formData, branch: newBranches, branch_data: newBranchData });
                              } else {
                                // Remove branch
                                if (formData.branch.length > 1) {
                                  const newBranches = formData.branch.filter(b => b !== branchOption.name);
                                  const newBranchData = { ...formData.branch_data };
                                  delete newBranchData[branchOption.name];
                                  
                                  setFormData({ ...formData, branch: newBranches, branch_data: newBranchData });
                                } else {
                                  toast.error("At least one branch must be selected");
                                }
                              }
                            }}
                          />
                          <label
                            htmlFor={`branch-${branchOption.name}`}
                            className="text-md3-body-small font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {branchOption.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {editMode && (
                    <div className="space-y-2">
                      <Label htmlFor="status" className="text-md3-label-large text-gray-700 font-medium">Status *</Label>
                      <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                        <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yet_to_start">Yet to Start</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="waiting_for_approval_returns">Waiting for Approval Returns</SelectItem>
                          <SelectItem value="hold_for_rfi">HOLD for RFI</SelectItem>
                          <SelectItem value="hold_by_client">HOLD by Client</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Awarded Date - Separate row if exists */}
                  {formData.estimation_id !== "" && formData.awarded_date && (
                    <div className="space-y-2">
                      <Label htmlFor="awarded_date" className="text-md3-label-large text-gray-700 font-medium">Awarded Date</Label>
                      <DatePicker
                        id="awarded_date"
                        value={formData.awarded_date}
                        disabled
                        className="bg-gray-100 cursor-not-allowed"
                      />
                      <p className="text-xs text-md3-on-surface-variant">From estimation (read-only)</p>
                    </div>
                  )}

                  {/* Start Date and End Date - Always on same row with proper spacing */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_date" className="text-md3-label-large text-gray-700 font-medium">Start Date *</Label>
                      <DatePicker
                        id="start_date"
                        data-testid="project-start-date-input"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="end_date" className="text-md3-label-large text-gray-700 font-medium">End Date</Label>
                      <DatePicker
                        id="end_date"
                        data-testid="project-end-date-input"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        disableWeekends={true}
                      />
                      <p className="text-xs text-md3-on-surface-variant">
                        Auto-calculated (editable)
                      </p>
                    </div>
                  </div>

                  {/* Scope of Work Section */}
                  <div className="col-span-2 space-y-3 p-4 border border-gray-300 rounded-md3-lg bg-gray-50">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-purple-900">Scope of Work</h3>
                      <Button type="button" size="sm" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={addScope}>
                        + Add Scope
                      </Button>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-md3-body-small">
                        <span className="text-md3-on-surface-variant">Total Weightage:</span>
                        <span className={`font-semibold ${
                          Math.abs(getTotalScopeWeightage() - 100) < 0.01 && getTotalScopeWeightage() > 0
                            ? 'text-green-600' 
                            : getTotalScopeWeightage() === 0 
                            ? 'text-md3-on-surface-variant'
                            : 'text-red-600'
                        }`}>
                          {getTotalScopeWeightage().toFixed(2)}% / 100%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full transition-all ${
                            Math.abs(getTotalScopeWeightage() - 100) < 0.01 && getTotalScopeWeightage() > 0
                              ? 'bg-green-500' 
                              : getTotalScopeWeightage() > 100
                              ? 'bg-red-500'
                              : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min(getTotalScopeWeightage(), 100)}%` }}
                        />
                      </div>
                      {parseFloat(formData.estimated_hours || 0) === 0 && (
                        <p className="text-xs text-md3-on-surface-variant">No estimated hours - scope weightage can be 0%</p>
                      )}
                      {parseFloat(formData.estimated_hours || 0) > 0 && Math.abs(getTotalScopeWeightage() - 100) > 0.01 && getTotalScopeWeightage() > 0 && (
                        <p className="text-xs text-red-600">⚠️ Total must equal 100%</p>
                      )}
                    </div>

                    {/* Scope Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-md3-body-small border-collapse">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-purple-300 p-2 text-left w-2/3">Scope Name</th>
                            <th className="border border-purple-300 p-2 text-right w-1/6">Weightage %</th>
                            <th className="border border-purple-300 p-2 text-center w-1/12">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.scope_of_work.map((scope) => (
                            <tr key={scope.id} className="bg-white">
                              <td className="border border-gray-300 p-2">
                                <Input value={scope.name}
                                  onChange={(e) => handleScopeNameChange(scope.id, e.target.value)}
                                  className="text-md3-body-small h-8"
                                  placeholder="Scope name"
                                />
                              </td>
                              <td className="border border-gray-300 p-2">
                                <Input type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={scope.weightage === 0 ? "" : scope.weightage}
                                  onChange={(e) => handleScopeChange(scope.id, 'weightage', e.target.value)}
                                  className="text-md3-body-small h-8 text-right"
                                  placeholder="0"
                                />
                              </td>
                              <td className="border border-gray-300 p-2 text-center">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => deleteScope(scope.id)}
                                  className="h-6 w-6 p-0 bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Purchase Order Number - Smaller box, last line */}
                  <div className="space-y-2">
                    <Label htmlFor="purchase_order_number" className="text-md3-label-large text-gray-700 font-medium">Purchase Order Number</Label>
                    <Input id="purchase_order_number"
                      placeholder="Enter PO Number"
                      value={formData.purchase_order_number}
                      onChange={(e) => setFormData({ ...formData, purchase_order_number: e.target.value })}
                      className="max-w-xs"
                    />
                  </div>

                  {/* Old Project Section */}
                  <div className="space-y-4 border-t pt-4 mt-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="is_old_project"
                        checked={formData.is_old_project}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          is_old_project: checked,
                          hours_already_spent: checked ? formData.hours_already_spent : ""
                        })}
                      />
                      <Label htmlFor="is_old_project" className="text-md3-body-small font-medium cursor-pointer">
                        Old Project (Mid-progress at deployment)
                      </Label>
                    </div>
                    
                    {formData.is_old_project && (
                      <div className="space-y-2 ml-6">
                        <Label htmlFor="hours_already_spent" className="text-md3-label-large text-gray-700 font-medium">Hours Already Spent *</Label>
                        <Input id="hours_already_spent"
                          type="number"
                          min="0"
                          max={formData.allocated_hours || 0}
                          step="1"
                          placeholder="Enter hours already spent"
                          value={formData.hours_already_spent}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // Keep empty string if user clears the field
                            if (inputValue === "") {
                              setFormData({ ...formData, hours_already_spent: "" });
                              return;
                            }
                            const value = parseInt(inputValue) || 0;
                            // Validate that parseInt succeeded (not NaN)
                            if (isNaN(parseInt(inputValue))) {
                              return; // Ignore invalid input
                            }
                            const maxHours = parseFloat(formData.allocated_hours) || 0;
                            if (value <= maxHours) {
                              setFormData({ ...formData, hours_already_spent: value });
                            } else {
                              toast.error(`Hours cannot exceed allocated hours (${maxHours})`);
                            }
                          }}
                          required={formData.is_old_project}
                          disabled={viewingProject?.hours_already_spent_approved && !canApprove()}
                          className="max-w-xs"
                        />
                        {formData.allocated_hours && (
                          <p className="text-xs text-md3-on-surface-variant">
                            Maximum: {formData.allocated_hours} hours (allocated hours)
                          </p>
                        )}
                        
                        {/* Approval Status */}
                        {viewingProject?.hours_already_spent_approved && (
                          <div className="flex items-center space-x-2 text-md3-body-small text-green-600 bg-green-50 p-2 rounded max-w-lg">
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span>
                              Approved by {users.find(u => u.id === viewingProject.hours_already_spent_approved_by)?.first_name || 'Unknown'} 
                              {viewingProject.hours_already_spent_approved_at && 
                                ` on ${new Date(viewingProject.hours_already_spent_approved_at).toLocaleDateString()}`
                              }
                            </span>
                          </div>
                        )}
                        
                        {/* Approve Button - requires approve permission */}
                        {editMode && viewingProject && formData.is_old_project && !viewingProject.hours_already_spent_approved && canApprove() && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleApproveHoursAlreadySpent}
                            className="max-w-xs border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                          >
                            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Approve Hours Already Spent
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                  </TabsContent>
                  
                  {/* Branch Allocation Tab */}
                  <TabsContent value="branches" className="space-y-6">
                    <div className="p-4 bg-gray-50 rounded-md3-lg">
                      <h3 className="text-md3-title-large mb-4">Branch Allocation</h3>
                      <p className="text-md3-body-small text-md3-on-surface-variant mb-4">
                        View and edit hours and cost allocated to each branch
                      </p>
                      
                      {formData.branch_data && Object.keys(formData.branch_data).length > 0 ? (
                        <div className="space-y-6">
                          {Object.entries(formData.branch_data).map(([branchName, allocation]) => (
                            <div key={branchName} className="bg-white p-4 rounded-md3-lg border border-md3-outline-variant">
                              <h4 className="font-semibold text-lg mb-3 text-blue-900">{branchName}</h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor={`${branchName}-hours`}>Allocated Hours</Label>
                                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`${branchName}-hours`}
                                    type="number"
                                    step="0.01"
                                    value={allocation.allocated_hours ?? ""}
                                    onChange={(e) => {
                                      const newBranchData = { ...formData.branch_data };
                                      newBranchData[branchName].allocated_hours = e.target.value === "" ? null : parseFloat(e.target.value);
                                      setFormData({ ...formData, branch_data: newBranchData });
                                    }}
                                    placeholder="Enter hours"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`${branchName}-cost`}>Allocated Cost ($)</Label>
                                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`${branchName}-cost`}
                                    type="number"
                                    step="0.01"
                                    value={allocation.allocated_cost ?? ""}
                                    onChange={(e) => {
                                      const newBranchData = { ...formData.branch_data };
                                      newBranchData[branchName].allocated_cost = e.target.value === "" ? null : parseFloat(e.target.value);
                                      setFormData({ ...formData, branch_data: newBranchData });
                                    }}
                                    placeholder="Enter cost"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`${branchName}-weeks`}>Allocated Schedule (Weeks)</Label>
                                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`${branchName}-weeks`}
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={allocation.allocated_schedule_weeks ?? ""}
                                    onChange={(e) => {
                                      const newBranchData = { ...formData.branch_data };
                                      newBranchData[branchName].allocated_schedule_weeks = e.target.value === "" ? null : parseInt(e.target.value);
                                      setFormData({ ...formData, branch_data: newBranchData });
                                    }}
                                    placeholder="Enter weeks"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`${branchName}-days`}>Allocated Schedule (Days)</Label>
                                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id={`${branchName}-days`}
                                    type="number"
                                    step="1"
                                    min="0"
                                    max="6"
                                    value={allocation.allocated_schedule_days ?? ""}
                                    onChange={(e) => {
                                      const newBranchData = { ...formData.branch_data };
                                      newBranchData[branchName].allocated_schedule_days = e.target.value === "" ? null : parseInt(e.target.value);
                                      setFormData({ ...formData, branch_data: newBranchData });
                                    }}
                                    placeholder="Enter days"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Status</Label>
                                  <Select
                                    value={allocation.status || "In Progress"}
                                    onValueChange={(value) => {
                                      const newBranchData = { ...formData.branch_data };
                                      newBranchData[branchName].status = value;
                                      setFormData({ ...formData, branch_data: newBranchData });
                                    }}
                                  >
                                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Not Started">Not Started</SelectItem>
                                      <SelectItem value="In Progress">In Progress</SelectItem>
                                      <SelectItem value="Completed">Completed</SelectItem>
                                      <SelectItem value="On Hold">On Hold</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {/* Summary */}
                          <div className="bg-white p-4 rounded-md3-lg border border-gray-300">
                            <h4 className="font-semibold mb-2">Total Allocation</h4>
                            <div className="grid grid-cols-3 gap-4 text-md3-body-small">
                              <div>
                                <span className="text-md3-on-surface-variant">Total Hours: </span>
                                <span className="font-bold">
                                  {Object.values(formData.branch_data).reduce((sum, alloc) => sum + (alloc.allocated_hours || 0), 0).toFixed(2)}
                                </span>
                                <span className="text-md3-on-surface-variant ml-2">/ {formData.allocated_hours || 0}</span>
                              </div>
                              <div>
                                <span className="text-md3-on-surface-variant">Total Cost: </span>
                                <span className="font-bold">
                                  ${Object.values(formData.branch_data).reduce((sum, alloc) => sum + (alloc.allocated_cost || 0), 0).toFixed(2)}
                                </span>
                                <span className="text-md3-on-surface-variant ml-2">/ ${formData.base_price || 0}</span>
                              </div>
                              <div>
                                <span className="text-md3-on-surface-variant">Total Schedule (Max): </span>
                                <span className="font-bold">
                                  {(() => {
                                    // Calculate max schedule (parallel execution)
                                    const allWeeks = Object.values(formData.branch_data).map(alloc => (alloc.allocated_schedule_weeks || 0));
                                    const allDays = Object.values(formData.branch_data).map(alloc => (alloc.allocated_schedule_days || 0));
                                    const maxWeeks = Math.max(...allWeeks, 0);
                                    const maxDays = Math.max(...allDays, 0);
                                    return `${maxWeeks}w ${maxDays}d`;
                                  })()}
                                </span>
                                <span className="text-md3-on-surface-variant ml-2">/ {formData.schedule_weeks || 0}w {formData.schedule_days || 0}d</span>
                                <p className="text-xs text-blue-600 mt-1">(Parallel execution - shows longest branch)</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-md3-on-surface-variant text-center py-8">
                          No branch allocation data available. This is a single-branch project.
                        </p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
                
                {/* Additional Notes from Client */}
                <div className="space-y-2 mt-4">
                  <Label htmlFor="client_notes_project_start" className="text-md3-label-large text-gray-700 font-medium">Additional notes from client (Project start)</Label>
                  <Textarea id="client_notes_project_start"
                    placeholder="Enter any additional notes or instructions from client at project start..."
                    value={formData.client_notes_project_start || ""}
                    onChange={(e) => setFormData({ ...formData, client_notes_project_start: e.target.value })}
                    rows={4}
                    className="resize-y"
                  />
                  <p className="text-xs text-md3-on-surface-variant">Record any special instructions or notes from the client regarding project initiation</p>
                </div>
                
                <Button type="submit" disabled={!editMode && isDuplicateProjectNumber(projects, formData.project_number)} className="w-full mt-4 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" data-testid="project-submit-button">
                  {editMode ? "Update Project" : "Create Project"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Branch Allocation Dialog */}
          <Dialog open={branchAllocationDialogOpen} onOpenChange={setBranchAllocationDialogOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Allocate Resources Across Branches</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-gray-600">
                  Distribute hours, cost, and schedule among selected branches
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Total Project Budget Summary */}
                <div className="bg-white p-4 rounded-md3-md border border-gray-300">
                  <h3 className="font-semibold text-md3-body-small mb-2">Total Project Budget:</h3>
                  <div className="grid grid-cols-3 gap-4 text-md3-body-small">
                    <div>
                      <span className="text-md3-on-surface-variant">Allocated Hours:</span>
                      <span className="ml-2 font-semibold">{formData.allocated_hours}h</span>
                    </div>
                    <div>
                      <span className="text-md3-on-surface-variant">Base Price:</span>
                      <span className="ml-2 font-semibold">${parseFloat(formData.base_price || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-md3-on-surface-variant">Schedule:</span>
                      <span className="ml-2 font-semibold">
                        {formData.allocated_schedule_weeks}w {formData.allocated_schedule_days}d
                      </span>
                    </div>
                  </div>
                </div>

                {/* Branch Allocation Forms */}
                {formData.branch.map((branch, index) => (
                  <div key={branch} className="border rounded-md3-lg p-4 space-y-3">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Badge variant="outline">{branch}</Badge>
                    </h3>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Allocated Hours */}
                      <div>
                        <Label>Allocated Hours *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="number"
                          min="0"
                          step="0.5"
                          value={branchAllocations[branch]?.allocated_hours || ""}
                          onChange={(e) => handleBranchAllocationChange(branch, 'allocated_hours', parseFloat(e.target.value) || 0)}
                          placeholder="Hours"
                        />
                        <p className="text-xs text-md3-on-surface-variant mt-1">
                          {((branchAllocations[branch]?.allocated_hours || 0) / (parseFloat(formData.allocated_hours) || 1) * 100).toFixed(0)}% of total
                        </p>
                      </div>

                      {/* Allocated Cost */}
                      <div>
                        <Label>Allocated Cost (USD) *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="number"
                          min="0"
                          step="100"
                          value={branchAllocations[branch]?.allocated_cost || ""}
                          onChange={(e) => handleBranchAllocationChange(branch, 'allocated_cost', parseFloat(e.target.value) || 0)}
                          placeholder="Cost"
                        />
                        <p className="text-xs text-md3-on-surface-variant mt-1">
                          {((branchAllocations[branch]?.allocated_cost || 0) / (parseFloat(formData.base_price) || 1) * 100).toFixed(0)}% of total
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Schedule */}
                      <div className="space-y-2">
                        <Label>Schedule</Label>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="number"
                              min="0"
                              placeholder="Weeks"
                              value={branchAllocations[branch]?.allocated_schedule_weeks || ""}
                              onChange={(e) => handleBranchAllocationChange(branch, 'allocated_schedule_weeks', parseInt(e.target.value) || 0)}
                            />
                          </div>
                          <div className="flex-1">
                            <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="number"
                              min="0"
                              max="4"
                              placeholder="Days"
                              value={branchAllocations[branch]?.allocated_schedule_days || ""}
                              onChange={(e) => handleBranchAllocationChange(branch, 'allocated_schedule_days', parseInt(e.target.value) || 0)}
                            />
                          </div>
                        </div>
                        <p className="text-xs text-md3-on-surface-variant">Weeks | Days (max 4)</p>
                      </div>

                      {/* Status */}
                      <div>
                        <Label>Initial Status</Label>
                        <Select 
                          value={branchAllocations[branch]?.status || "yet_to_start"}
                          onValueChange={(value) => handleBranchAllocationChange(branch, 'status', value)}
                        >
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yet_to_start">Yet to Start</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="hold_for_rfi">Hold For RFI</SelectItem>
                            <SelectItem value="hold_by_client">Hold by Client</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Start Date */}
                      <div>
                        <Label>Start Date</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="date"
                          value={branchAllocations[branch]?.start_date || formData.start_date}
                          onChange={(e) => handleBranchAllocationChange(branch, 'start_date', e.target.value)}
                        />
                      </div>

                      {/* End Date (Auto-calculated) */}
                      <div>
                        <Label>End Date (Auto-calculated)</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" type="date"
                          value={branchAllocations[branch]?.end_date || ""}
                          readOnly
                          
                        />
                      </div>
                    </div>

                    {/* Branch-specific error */}
                    {allocationErrors[branch] && (
                      <p className="text-md3-body-small text-red-600 mt-2">❌ {allocationErrors[branch]}</p>
                    )}
                  </div>
                ))}

                {/* Validation Summary */}
                <div className="bg-gray-50 p-4 rounded-md3-md border">
                  <h3 className="font-semibold text-md3-body-small mb-2">Allocation Summary:</h3>
                  <div className="space-y-1 text-md3-body-small">
                    <div className="flex justify-between">
                      <span>Total Hours:</span>
                      <span className={`font-semibold ${
                        Object.values(branchAllocations).reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_hours) || 0), 0) > parseFloat(formData.allocated_hours || 0)
                          ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {Object.values(branchAllocations).reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_hours) || 0), 0)}h / {formData.allocated_hours}h
                        {Object.values(branchAllocations).reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_hours) || 0), 0) <= parseFloat(formData.allocated_hours || 0) ? ' ✓' : ' ✗'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Cost:</span>
                      <span className={`font-semibold ${
                        Object.values(branchAllocations).reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_cost) || 0), 0) > parseFloat(formData.base_price || 0)
                          ? 'text-red-600' : 'text-green-600'
                      }`}>
                        ${Object.values(branchAllocations).reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_cost) || 0), 0).toLocaleString()} / ${parseFloat(formData.base_price || 0).toLocaleString()}
                        {Object.values(branchAllocations).reduce((sum, alloc) => sum + (parseFloat(alloc.allocated_cost) || 0), 0) <= parseFloat(formData.base_price || 0) ? ' ✓' : ' ✗'}
                      </span>
                    </div>
                  </div>

                  {/* Total error */}
                  {allocationErrors.total && (
                    <p className="text-md3-body-small text-red-600 mt-2">⚠️ {allocationErrors.total}</p>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setBranchAllocationDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="button" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleBranchAllocationSubmit}>
                    Create Project with Branches
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          </div>
        </div>

        {/* Filter Section */}
        <Card className="bg-md3-surface-container/50 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-md3-body-small font-semibold ">Filters</CardTitle>
              {/* Download/Upload buttons - hidden for Projects dept users */}
              {!isProjectsDeptUser() && (
              <div className="flex gap-2">
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const response = await api.get("/template/projects", { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'projects_template_v3.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Excel Template downloaded!");
                    } catch (error) {
                      toast.error("Failed to download template");
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Excel Template
                </Button>
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" 
                  size="sm"
                  onClick={() => document.getElementById('bulk-upload-projects').click()}
                  title="Upload Excel file (.xlsx or .xls)"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Bulk Upload Excel
                </Button>
                <input
                  id="bulk-upload-projects"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    // Validate file type
                    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                      toast.error("Please upload an Excel file (.xlsx or .xls)");
                      e.target.value = '';
                      return;
                    }
                    
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    try {
                      // Call preview endpoint
                      const response = await api.post("/import/projects/preview", formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      
                      setImportPreviewData(response.data);
                      setImportPreviewOpen(true);
                      e.target.value = '';
                    } catch (error) {
                      toast.error("Failed to preview Excel file. Please check file format and try again.");
                      e.target.value = '';
                    }
                  }}
                />
              </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="filter_client_name" className="text-xs">Client Name</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_client_name"
                  placeholder="Search by name..."
                  value={filters.client_name}
                  onChange={(e) => setFilters({ ...filters, client_name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="filter_project_number" className="text-xs">Project Number</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_project_number"
                  placeholder="Search project number..."
                  value={filters.project_number}
                  onChange={(e) => setFilters({ ...filters, project_number: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="filter_branch" className="text-xs">Branch</Label>
                <Select value={filters.branch || "all"} onValueChange={(value) => setFilters({ ...filters, branch: value === "all" ? "" : value })}>
                  <SelectTrigger id="filter_branch">
                    <SelectValue placeholder="All Branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    <SelectItem value="Hyderabad">Hyderabad</SelectItem>
                    <SelectItem value="Chennai">Chennai</SelectItem>
                    <SelectItem value="Vendor">Vendor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {user?.department !== "Projects" && (
                <div className="space-y-2">
                  <Label htmlFor="filter_sales" className="text-xs">Sales</Label>
                  <Select value={filters.sales || "all"} onValueChange={(value) => setFilters({ ...filters, sales: value === "all" ? "" : value })}>
                    <SelectTrigger id="filter_sales">
                      <SelectValue placeholder="All Sales" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sales</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name || ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {user?.department !== "Projects" && (
                <div className="space-y-2">
                  <Label htmlFor="filter_estimation_number" className="text-xs">Estimation Number</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="filter_estimation_number"
                    placeholder="Search by number..."
                    value={filters.estimation_number}
                    onChange={(e) => setFilters({ ...filters, estimation_number: e.target.value })}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="filter_status" className="text-xs">Status</Label>
                <Select
                  value={filters.status || "all"}
                  onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}
                >
                  <SelectTrigger id="filter_status" className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" data-testid="filter-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="yet_to_start">Yet to Start</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_for_approval_returns">Waiting for Approval Returns</SelectItem>
                    <SelectItem value="hold_for_rfi">HOLD for RFI</SelectItem>
                    <SelectItem value="hold_by_client">HOLD by Client</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-md3-on-surface-variant">
                Showing {filteredProjects.length} of {projects.length} projects
              </p>
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" size="sm" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="h-48 bg-white" />
              </Card>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <Card className="bg-white shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
            <CardContent className="py-12 text-center">
              <p className="text-md3-on-surface-variant">No projects yet. Create your first project!</p>
            </CardContent>
          </Card>
        ) : filteredProjects.length === 0 ? (
          <Card className="bg-white shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
            <CardContent className="py-12 text-center">
              <p className="text-md3-on-surface-variant">No projects match your filters.</p>
              <Button variant="link" onClick={clearFilters} className="mt-2">
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
            <CardHeader>
              <CardTitle className="text-md3-title-large text-md3-on-surface ">All Projects ({filteredProjects.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Project Number</TableHead>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Branch</TableHead>
                      {/* Sales column - hidden for Projects dept users */}
                      {!isProjectsDeptUser() && <TableHead>Sales</TableHead>}
                      <TableHead>Status</TableHead>
                      <TableHead>Start Date</TableHead>
                      {canViewCosts() && <TableHead>Total Value (USD)</TableHead>}
                      {/* Hours column - hidden for Projects dept users */}
                      {!isProjectsDeptUser() && <TableHead>Hours</TableHead>}
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paged.pageItems.map((project) => (
                      <TableRow 
                        key={project.id}
                        className="hover:bg-gray-50"
                        data-testid={`project-row-${project.id}`}
                      >
                        <TableCell className="font-medium">
                          <div>
                            <RouterLink to={`/projects/${project.id}`} className="text-md3-primary hover:underline" data-testid={`project-number-link-${project.id}`}>
                              {project.project_number || project.id.substring(0, 8)}
                            </RouterLink>
                            {project.estimation_number && (
                              <div className="text-xs text-md3-on-surface-variant mt-0.5">
                                ({project.estimation_number}{project.estimation_version ? ` v${project.estimation_version}` : ""})
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <RouterLink to={`/projects/${project.id}`} className="font-medium hover:text-md3-primary hover:underline" data-testid={`project-name-link-${project.id}`}>
                            {project.name}
                          </RouterLink>
                          {awardSyncIds.has(project.id) && (
                            <Badge
                              className="mt-1 bg-orange-100 text-orange-800 border border-orange-300 hover:bg-orange-100"
                              data-testid={`award-update-pending-badge-${project.id}`}
                            >
                              Award update pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-md3-body-small">
                          {project.client_id ? getClientName(project.client_id) : project.client_name || "Unknown"}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const branches = Array.isArray(project.branch) ? project.branch : [project.branch];
                            if (branches.length > 1) {
                              return <Badge variant="outline">Multiple</Badge>;
                            }
                            return <Badge variant="outline">{branches[0] || "N/A"}</Badge>;
                          })()}
                        </TableCell>
                        {/* Sales cell - hidden for Projects dept users */}
                        {!isProjectsDeptUser() && (
                          <TableCell className="text-md3-body-small">
                            {project.sales ? getUserName(project.sales) : "-"}
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge className={getStatusColor(project.status)}>
                            {formatStatus(project.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-md3-body-small">
                          {format(new Date(project.start_date), "MMM dd, yyyy")}
                        </TableCell>
                        {canViewCosts() && (
                          <TableCell className="text-md3-body-small font-medium text-green-600">
                            {getTotalProjectValue(project).toLocaleString()}
                          </TableCell>
                        )}
                        {/* Hours cell - hidden for Projects dept users */}
                        {!isProjectsDeptUser() && (
                          <TableCell className="text-md3-body-small">
                            {project.estimated_hours || 0}h
                          </TableCell>
                        )}
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            {/* Edit button - requires edit permission and not Projects dept user */}
                            {canEdit() && !isProjectsDeptUser() && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                onClick={() => handleEdit(project)}
                                title="Edit Project"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              onClick={() => navigate(`/projects/${project.id}`)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              onClick={() => openSubmittalsDialog(project.id)}
                              title="Manage Submittals"
                            >
                              <ClipboardList className="h-4 w-4" />
                            </Button>
                            {project.has_newer_estimation_version && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="hover:bg-emerald-50 text-emerald-700 rounded-md3-full transition-all duration-200 relative"
                                onClick={() => openEstimationSync(project)}
                                title="Newer estimation version available — click to sync"
                                data-testid={`project-sync-btn-${project.id}`}
                              >
                                <RefreshCw className="h-4 w-4" />
                                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white" />
                              </Button>
                            )}
                            {/* Delete button - requires delete permission */}
                            {canDelete() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="hover:bg-red-100 text-red-600 rounded-md3-full transition-all duration-200"
                                onClick={() => handleDeleteProject(project)}
                                title="Delete Project"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  currentPage={paged.page}
                  totalItems={paged.total}
                  pageSize={paged.pageSize}
                  onPageChange={paged.onPageChange}
                  onPageSizeChange={paged.onPageSizeChange}
                  pageSizeOptions={[25, 50, 100]}
                  testIdPrefix="projects-pagination"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Submittals Dialog */}
        <Dialog open={submittalsDialogOpen} onOpenChange={setSubmittalsDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between text-md3-headline-small text-md3-on-surface font-semibold">
                <span>Project Submittals & Schedule Tracker</span>
              </DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Manage submittal tracking for this project
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Scope-based Organization */}
              {(() => {
                const currentProject = currentProjectFull || projects.find(p => p.id === currentProjectId);
                const scopes = currentProject?.scope_of_work || [];
                const projectCompletionPercentage = calculateProjectCompletionPercentage(scopes);
                
                return (
                  <>
                    {/* Project Completion Percentage Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-md3-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-md3-title-large text-blue-900">Project Completion Percentage (Detailing)</h3>
                          <p className="text-md3-body-small text-blue-600 mt-1">Weighted average of all scope completions</p>
                        </div>
                        <div className="text-right">
                          <div className="text-md3-display-small text-md3-on-surface text-blue-700">
                            {projectCompletionPercentage.toFixed(1)}%
                          </div>
                          <p className="text-xs text-blue-500 mt-1">Overall Progress</p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
              
              {(() => {
                const currentProject = currentProjectFull || projects.find(p => p.id === currentProjectId);
                const scopes = currentProject?.scope_of_work || [];
                
                // Group submittals by scope
                const submittalsByScope = {};
                const unlinkedSubmittals = [];
                
                editedSubmittals.forEach(submittal => {
                  if (submittal.scope_of_work_id) {
                    if (!submittalsByScope[submittal.scope_of_work_id]) {
                      submittalsByScope[submittal.scope_of_work_id] = [];
                    }
                    submittalsByScope[submittal.scope_of_work_id].push(submittal);
                  } else {
                    unlinkedSubmittals.push(submittal);
                  }
                });

                return (
                  <>
                    {/* Update Button */}
                    {hasUnsavedChanges && (
                      <div className="flex justify-end">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={saveAllSubmittals}
                          className="bg-md3-primary hover:bg-md3-primary/90 shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                        >
                          Update
                        </Button>
                      </div>
                    )}

                    {/* Scope-based Submittal Organization */}
                    {loadingSubmittals ? (
                      <div className="text-center py-8">Loading submittals...</div>
                    ) : scopes.length === 0 ? (
                      <div className="text-center py-8 text-md3-on-surface-variant">
                        No scopes defined. Please add scopes to the project first.
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Display each scope with its submittals */}
                        {scopes.map((scope) => {
                          const scopeSubmittals = submittalsByScope[scope.id] || [];
                          return (
                            <div key={scope.id} className="border-2 border-gray-300 rounded-md3-lg p-4 bg-gray-50">
                              {/* Scope Header */}
                              <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center gap-3">
                                  <h3 className="text-md3-title-large text-purple-900">{scope.name}</h3>
                                  <Badge variant="outline" className="bg-white">
                                    Scope: {scope.weightage || scope.total_weightage || 0}%
                                  </Badge>
                                  {/* Total Submittal Weightage */}
                                  <Badge 
                                    variant="outline" 
                                    className={`${
                                      Math.abs(calculateScopeWeightage(scope.id) - 100) < 0.01 
                                        ? 'bg-green-100 text-green-700 border-green-300' 
                                        : calculateScopeWeightage(scope.id) > 100
                                        ? 'bg-red-100 text-red-700 border-red-300'
                                        : 'bg-yellow-100 text-yellow-700 border-yellow-300'
                                    }`}
                                  >
                                    Submittals: {calculateScopeWeightage(scope.id).toFixed(1)}% / 100%
                                  </Badge>
                                  {/* Scope Completion Percentage */}
                                  <Badge 
                                    variant="outline" 
                                    className="bg-blue-100 text-blue-700 border-blue-300"
                                  >
                                    Completion: {calculateScopeCompletionPercentage(scope.id).toFixed(1)}%
                                  </Badge>
                                </div>
                                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                  size="sm"
                                  onClick={() => {
                                    const name = prompt(`Enter submittal name for ${scope.name}:`);
                                    if (name) addNewSubmittal(name, scope.id);
                                  }}
                                >
                                  <Plus className="mr-2 h-4 w-4" /> Add Submittal
                                </Button>
                              </div>

                              {/* Submittals under this scope */}
                              {scopeSubmittals.length === 0 ? (
                                <div className="text-center py-4 text-md3-on-surface-variant text-md3-body-small bg-white rounded border border-dashed border-purple-300">
                                  No submittals yet. Click "Add Submittal" to create one.
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  {scopeSubmittals.map((submittal) => (
                    <Card key={submittal.id} className="border-2">
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            {/* Editable Submittal Name */}
                            <Input value={submittal.submittal_name}
                              onChange={(e) => updateSubmittalLocal(submittal.id, { submittal_name: e.target.value })}
                              className="text-md3-title-large border-none shadow-none px-0 focus-visible:ring-0"
                            />
                            {/* Weightage Input */}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-md3-body-small text-md3-on-surface-variant">Weightage:</span>
                              <Input type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={submittal.weightage || 0}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  updateSubmittalLocal(submittal.id, { weightage: value });
                                }}
                                className="w-20 h-8 text-md3-body-small"
                              />
                              <span className="text-md3-body-small text-md3-on-surface-variant">%</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {/* Status Override: None / HOLD / Completed (mutually exclusive radios) */}
                            {(() => {
                              // "Mark Completed" requires the Approval planned date to be set first.
                              const approvalStep = (submittal.steps || []).find(s => s.step_name === "Approval");
                              const canForceComplete = !!approvalStep && !!approvalStep.planned_date;
                              // Hide both radios when the submittal has been completed via the
                              // normal workflow (Fab full submitted etc.) — overrides are only
                              // relevant for in-flight submittals.
                              const naturallyCompleted =
                                calculateSubmittalStatus(submittal) === "Completed" &&
                                !submittal.manually_completed;
                              if (naturallyCompleted) {
                                return (
                                  <div
                                    className="flex items-center gap-1.5 text-md3-body-small text-green-700"
                                    data-testid={`submittal-naturally-completed-${submittal.id}`}
                                    title="This submittal completed through the normal workflow. Overrides are not needed."
                                  >
                                    <CheckCircle2 className="h-4 w-4" />
                                    <span className="font-medium">Completed</span>
                                  </div>
                                );
                              }
                              return (
                                <div className="flex items-center gap-3" data-testid={`submittal-override-${submittal.id}`}>
                                  {/* HOLD radio — hidden once the submittal is force-completed
                                      (overrides are mutually exclusive; HOLD makes no sense
                                      on a completed submittal). */}
                                  {!submittal.manually_completed && (
                                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                      <input
                                        type="radio"
                                        name={`override-${submittal.id}`}
                                        checked={!!submittal.hold_enabled && !submittal.manually_completed}
                                        onChange={() => {
                                          updateSubmittalLocal(submittal.id, {
                                            hold_enabled: true,
                                            hold_reason: submittal.hold_reason || "RFI",
                                            manually_completed: false,
                                            manually_completed_note: null,
                                          });
                                        }}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                        data-testid={`submittal-radio-hold-${submittal.id}`}
                                      />
                                      <span className="text-md3-body-small font-medium">HOLD</span>
                                    </label>
                                  )}
                                  {/* Mark Completed radio — only enabled when Approval planned_date exists */}
                                  {canForceComplete ? (
                                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                      <input
                                        type="radio"
                                        name={`override-${submittal.id}`}
                                        checked={!!submittal.manually_completed}
                                        onChange={() => {
                                          // Already manually-completed → no-op (use Clear to revert)
                                          if (submittal.manually_completed) return;
                                          // Open note dialog; only on confirm we'll flip the flag
                                          setForceCompleteDialog({ open: true, submittalId: submittal.id, note: "" });
                                        }}
                                        className="h-4 w-4 text-green-600 focus:ring-green-500 cursor-pointer"
                                        data-testid={`submittal-radio-completed-${submittal.id}`}
                                      />
                                      <span className="text-md3-body-small font-medium">Mark Completed</span>
                                    </label>
                                  ) : (
                                    <span
                                      className="flex items-center gap-1.5 opacity-50 cursor-not-allowed select-none"
                                      title="Set the Approval planned date before marking the submittal as completed."
                                      data-testid={`submittal-mark-completed-disabled-${submittal.id}`}
                                    >
                                      <input type="radio" disabled className="h-4 w-4" />
                                      <span className="text-md3-body-small font-medium">Mark Completed</span>
                                    </span>
                                  )}
                                  {/* Clear override (only visible when one is selected) */}
                                  {(submittal.hold_enabled || submittal.manually_completed) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        updateSubmittalLocal(submittal.id, {
                                          hold_enabled: false,
                                          hold_reason: null,
                                          manually_completed: false,
                                          manually_completed_note: null,
                                        });
                                      }}
                                      className="text-xs text-md3-on-surface-variant underline cursor-pointer hover:text-md3-primary"
                                      data-testid={`submittal-clear-override-${submittal.id}`}
                                    >
                                      Clear
                                    </button>
                                  )}
                                  {/* HOLD reason dropdown */}
                                  {submittal.hold_enabled && !submittal.manually_completed && (
                                    <Select
                                      value={submittal.hold_reason || "RFI"}
                                      onValueChange={(value) => updateSubmittalLocal(submittal.id, { hold_reason: value })}
                                    >
                                      <SelectTrigger className="w-28 h-8">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="RFI">RFI</SelectItem>
                                        <SelectItem value="Client">Client</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  )}
                                </div>
                              );
                            })()}
                            
                            {/* Auto-calculated Status (Read-only) */}
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                              calculateSubmittalStatus(submittal) === "App-Progress" ? "bg-blue-100 text-blue-700" :
                              calculateSubmittalStatus(submittal) === "BFA waiting" ? "bg-yellow-100 text-yellow-700" :
                              calculateSubmittalStatus(submittal) === "Fab-Progress" ? "bg-gray-100 text-purple-700" :
                              calculateSubmittalStatus(submittal) === "Completed" ? "bg-green-100 text-green-700" :
                              calculateSubmittalStatus(submittal) === "HOLD due to RFI" ? "bg-orange-100 text-orange-700" :
                              calculateSubmittalStatus(submittal) === "HOLD by Client" ? "bg-red-100 text-red-700" :
                              "bg-white text-md3-on-surface-variant"
                            }`}>
                              {calculateSubmittalStatus(submittal)}
                            </span>
                            
                            {/* Manual-complete indicator badge — distinguishes force-complete from naturally-complete */}
                            {submittal.manually_completed && (
                              <button
                                type="button"
                                onClick={() => setViewForceCompleteDialog({ open: true, submittal })}
                                title="View force-complete note"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap hover:bg-amber-200 cursor-pointer"
                                data-testid={`submittal-manual-complete-badge-${submittal.id}`}
                              >
                                <CheckCircle2 className="h-3 w-3" />
                                Force-Completed
                              </button>
                            )}
                            
                            {/* Completion Percentage */}
                            <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 whitespace-nowrap">
                              {calculateCompletionPercentage(submittal, currentClientBilling.approval, currentClientBilling.fabrication).toFixed(1)}%
                            </span>
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                              onClick={() => setExpandedSubmittal(expandedSubmittal === submittal.id ? null : submittal.id)}
                            >
                              {expandedSubmittal === submittal.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                              onClick={() => deleteSubmittal(submittal.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>

                      {expandedSubmittal === submittal.id && (
                        <CardContent>
                          <div className="space-y-6">
                            {/* Steps Table */}
                            <Table className="bg-md3-surface-container rounded-md3-md">
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Step</TableHead>
                                  <TableHead>Planning Status</TableHead>
                                  <TableHead>Planned Date</TableHead>
                                  <TableHead>Actual Date</TableHead>
                                  <TableHead>Received Date</TableHead>
                                  <TableHead>Submission Complete</TableHead>
                                  <TableHead>Return Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {submittal.steps.map((step, stepIndex) => {
                                  // Get approval step to check if submitted
                                  const approvalStep = submittal.steps.find(s => s.step_name === "Approval");
                                  const backFromApprovalStep = submittal.steps.find(s => s.step_name === "Back from Approval");
                                  const isApprovalSubmitted = calculatePlanningStatus(approvalStep || {}) === "Submitted";
                                  const isBackFromApprovalReceived = calculatePlanningStatus(backFromApprovalStep || {}) === "Received";
                                  
                                  // Hide Back from Approval if Approval not submitted
                                  if (step.step_name === "Back from Approval" && !isApprovalSubmitted) {
                                    return null;
                                  }
                                  
                                  // Handle Re-approval visibility: Re-approval X only visible if Back from Re-approval (X-1) is received
                                  if (step.step_name && step.step_name.startsWith("Re-approval")) {
                                    const reapprovalNum = parseInt(step.step_name.match(/\d+/)[0]);
                                    if (reapprovalNum === 1) {
                                      // Re-approval 1 visible only if Back from Approval is received
                                      if (!isBackFromApprovalReceived) return null;
                                    } else {
                                      // Re-approval N visible only if Back from Re-approval (N-1) is received
                                      const prevBackFromReapproval = submittal.steps.find(s => s.step_name === `Back from Re-approval ${reapprovalNum - 1}`);
                                      if (calculatePlanningStatus(prevBackFromReapproval || {}) !== "Received") return null;
                                    }
                                  }
                                  
                                  // Handle Back from Re-approval visibility
                                  if (step.step_name && step.step_name.startsWith("Back from Re-approval")) {
                                    const reapprovalNum = parseInt(step.step_name.match(/\d+/)[0]);
                                    const correspondingReapproval = submittal.steps.find(s => s.step_name === `Re-approval ${reapprovalNum}`);
                                    // Only visible if corresponding Re-approval is submitted
                                    if (calculatePlanningStatus(correspondingReapproval || {}) !== "Submitted") return null;
                                  }
                                  
                                  // Hide Fabrication if Approval not submitted OR Back from Approval not received
                                  // OR if the most recent "Back from" step doesn't have return status as Approved/Approved as noted
                                  if (step.step_name === "Fabrication") {
                                    if (!isApprovalSubmitted || !isBackFromApprovalReceived) return null;
                                    
                                    // Find the most recent "Back from" step (could be Back from Approval or Back from Re-approval)
                                    const backFromSteps = submittal.steps.filter(s => s.step_name.startsWith("Back from"));
                                    const mostRecentBackFrom = backFromSteps[backFromSteps.length - 1];
                                    
                                    // Check if most recent back from step has return status as Approved or Approved as noted
                                    if (mostRecentBackFrom) {
                                      const returnStatus = mostRecentBackFrom.return_status;
                                      if (returnStatus !== "Approved" && returnStatus !== "Approved as noted") {
                                        return null;
                                      }
                                    }
                                  }
                                  
                                  return (
                                    <TableRow key={stepIndex}>
                                      <TableCell className="font-medium">{step.step_name}</TableCell>
                                      <TableCell>
                                        {/* Read-only auto-calculated status badge */}
                                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                                          calculatePlanningStatus(step) === "Yet to start" ? "bg-white text-md3-on-surface-variant" :
                                          calculatePlanningStatus(step) === "Planned" ? "bg-blue-100 text-blue-700" :
                                          calculatePlanningStatus(step) === "Waiting" ? "bg-yellow-100 text-yellow-700" :
                                          calculatePlanningStatus(step) === "Received" ? "bg-green-100 text-green-700" :
                                          calculatePlanningStatus(step) === "Partially submitted" ? "bg-orange-100 text-orange-700" :
                                          calculatePlanningStatus(step) === "Submitted" ? "bg-green-100 text-green-700" :
                                          "bg-white text-md3-on-surface-variant"
                                        }`}>
                                          {calculatePlanningStatus(step)}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        {step.step_name && !step.step_name.startsWith("Back from") && (
                                          <DatePicker
                                            value={step.planned_date || ""}
                                            onChange={(e) => {
                                              const newSteps = [...submittal.steps];
                                              newSteps[stepIndex].planned_date = e.target.value;
                                              // Auto-calculate planning status
                                              newSteps[stepIndex].planning_status = calculatePlanningStatus(newSteps[stepIndex]);
                                              updateSubmittalLocal(submittal.id, { steps: newSteps });
                                            }}
                                            min={
                                              (step.step_name === "Fabrication" || step.step_name.startsWith("Fabrication "))
                                                ? getMinDateForFabrication(submittal)
                                                : step.step_name.startsWith("Re-approval")
                                                ? getMinDateForReapproval(submittal, stepIndex)
                                                : null
                                            }
                                            className="w-36"
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {step.step_name && !step.step_name.startsWith("Back from") && (
                                          <DatePicker
                                            value={step.actual_date || ""}
                                            onChange={(e) => {
                                              const newSteps = [...submittal.steps];
                                              newSteps[stepIndex].actual_date = e.target.value;
                                              // Auto-calculate planning status
                                              newSteps[stepIndex].planning_status = calculatePlanningStatus(newSteps[stepIndex]);
                                              updateSubmittalLocal(submittal.id, { steps: newSteps });
                                            }}
                                            min={
                                              (step.step_name === "Fabrication" || step.step_name.startsWith("Fabrication "))
                                                ? getMinDateForFabrication(submittal)
                                                : step.step_name.startsWith("Re-approval")
                                                ? getMinDateForReapproval(submittal, stepIndex)
                                                : null
                                            }
                                            className="w-36"
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {step.step_name && step.step_name.startsWith("Back from") && (
                                          <DatePicker
                                            value={step.received_date || ""}
                                            onChange={(e) => {
                                              const newSteps = [...submittal.steps];
                                              newSteps[stepIndex].received_date = e.target.value;
                                              // Auto-calculate planning status
                                              newSteps[stepIndex].planning_status = calculatePlanningStatus(newSteps[stepIndex]);
                                              updateSubmittalLocal(submittal.id, { steps: newSteps });
                                            }}
                                            min={getMinDateForBackFromApproval(submittal, stepIndex)}
                                            className="w-36"
                                          />
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {/* Submission Complete dropdown - Only for Fabrication steps when actual_date is filled */}
                                        {step.step_name && (step.step_name === "Fabrication" || step.step_name.startsWith("Fabrication ")) && step.actual_date && (
                                          <div className="flex items-center gap-2">
                                            {/* Only fabrication steps created by "Partially Approved" are forced to Partial */}
                                            {/* Fabrication steps created by partial submission get dropdown choice */}
                                            {step.created_by_partial_approval === true ? (
                                              <div className="text-md3-body-small font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded">
                                                Partial (Required)
                                              </div>
                                            ) : (
                                              <Select
                                                value={step.submission_complete || ""}
                                                onValueChange={(value) => handleSubmissionCompleteChange(submittal, stepIndex, value)}
                                              >
                                                <SelectTrigger className="w-32">
                                                  <SelectValue placeholder="Select" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="Full">Full</SelectItem>
                                                  <SelectItem value="Partial">Partial</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            )}
                                            {/* Percentage input - Only when Partial is selected */}
                                            {step.submission_complete === "Partial" && (
                                              <div className="flex items-center gap-1">
                                                <Input type="number"
                                                  min="0"
                                                  max="100"
                                                  step="0.1"
                                                  value={step.submission_percentage || ""}
                                                  onChange={(e) => handleSubmissionPercentageChange(submittal, stepIndex, e.target.value)}
                                                  placeholder="%"
                                                  className="w-20"
                                                />
                                                <span className="text-md3-body-small text-md3-on-surface-variant">%</span>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {step.step_name && step.step_name.startsWith("Back from") && calculatePlanningStatus(step) === "Received" && (
                                          <Select
                                            value={step.return_status || ""}
                                            onValueChange={(value) => handleReturnStatusChange(submittal, stepIndex, value)}
                                          >
                                            <SelectTrigger className="w-48">
                                              <SelectValue placeholder="Select return status" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="Approved">Approved</SelectItem>
                                              <SelectItem value="Approved as noted">Approved as noted</SelectItem>
                                              <SelectItem value="Partially Approved">Partially Approved</SelectItem>
                                              <SelectItem value="Revised and Resubmit">Revised and Resubmit</SelectItem>
                                              <SelectItem value="Rejected">Rejected</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      )}
                                    </Card>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Unlinked Submittals (legacy or no scope) */}
                        {unlinkedSubmittals.length > 0 && (
                          <div className="border-2 border-md3-outline-variant rounded-md3-lg p-4 bg-gray-50">
                            <div className="flex justify-between items-center mb-4">
                              <h3 className="text-md3-title-large text-md3-on-surface-variant">Unlinked Submittals</h3>
                              <Badge variant="outline">No Scope</Badge>
                            </div>
                            <div className="space-y-3">
                              {unlinkedSubmittals.map((submittal) => (
                                <Card key={submittal.id} className="border-2">
                                  <CardHeader className="pb-3">
                                    <div className="flex justify-between items-start gap-4">
                                      <div className="flex-1">
                                        <Input value={submittal.submittal_name}
                                          onChange={(e) => updateSubmittalLocal(submittal.id, { submittal_name: e.target.value })}
                                          className="text-md3-title-large border-none shadow-none px-0 focus-visible:ring-0"
                                        />
                                      </div>
                                      {/* Rest of submittal card same as before */}
                                    </div>
                                  </CardHeader>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="flex justify-end mt-4">
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => setSubmittalsDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Force-Complete Note Dialog: ask why before flipping the flag */}
        <Dialog open={forceCompleteDialog.open} onOpenChange={(open) => setForceCompleteDialog((d) => ({ ...d, open }))}>
          <DialogContent className="sm:max-w-md" data-testid="force-complete-dialog">
            <DialogHeader>
              <DialogTitle>Mark Submittal as Completed</DialogTitle>
              <DialogDescription>
                Force-completing bypasses the regular step workflow. Please record why so the team has context.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label htmlFor="force-complete-note">Reason / Note *</Label>
              <textarea
                id="force-complete-note"
                rows={4}
                className="w-full border border-md3-outline rounded-md3-sm p-2 text-sm focus:ring-2 focus:ring-md3-primary"
                placeholder="e.g. Client confirmed verbal sign-off via email on 2026-02-04; formal submission waived."
                value={forceCompleteDialog.note}
                onChange={(e) => setForceCompleteDialog((d) => ({ ...d, note: e.target.value }))}
                data-testid="force-complete-note-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setForceCompleteDialog({ open: false, submittalId: null, note: "" })}
                data-testid="force-complete-cancel"
              >Cancel</Button>
              <Button
                onClick={() => {
                  const note = (forceCompleteDialog.note || "").trim();
                  if (!note) {
                    toast.error("Please add a note before confirming.");
                    return;
                  }
                  updateSubmittalLocal(forceCompleteDialog.submittalId, {
                    manually_completed: true,
                    manually_completed_note: note,
                    hold_enabled: false,
                    hold_reason: null,
                  });
                  setForceCompleteDialog({ open: false, submittalId: null, note: "" });
                }}
                data-testid="force-complete-confirm"
              >Confirm</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Force-Complete Details Dialog */}
        <Dialog open={viewForceCompleteDialog.open} onOpenChange={(open) => setViewForceCompleteDialog((d) => ({ ...d, open }))}>
          <DialogContent className="sm:max-w-md" data-testid="force-complete-view-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-amber-700" />
                Force-Completed
              </DialogTitle>
              <DialogDescription>
                This submittal was marked complete outside the regular workflow.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm py-2">
              <div>
                <div className="text-xs text-md3-on-surface-variant">By</div>
                <div className="font-medium">{viewForceCompleteDialog.submittal?.manually_completed_by_name || "Unknown"}</div>
              </div>
              <div>
                <div className="text-xs text-md3-on-surface-variant">When</div>
                <div className="font-medium">
                  {viewForceCompleteDialog.submittal?.manually_completed_at
                    ? new Date(viewForceCompleteDialog.submittal.manually_completed_at).toLocaleString()
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-md3-on-surface-variant">Note</div>
                <div className="font-medium whitespace-pre-wrap bg-md3-surface-container-low p-2 rounded text-md3-on-surface">
                  {viewForceCompleteDialog.submittal?.manually_completed_note || "—"}
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => setViewForceCompleteDialog({ open: false, submittal: null })}
                data-testid="force-complete-view-close"
              >Close</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Preview Dialog */}
        <BulkImportPreview
          open={importPreviewOpen}
          onClose={() => setImportPreviewOpen(false)}
          previewData={importPreviewData}
          onApply={handleApplyImport}
          entityType="Projects"
        />

        {/* Estimation Sync Dialog */}
        <Dialog
          open={syncDialog.open}
          onOpenChange={(o) => !syncDialog.saving && setSyncDialog((d) => ({ ...d, open: o }))}
        >
          <DialogContent className="max-w-2xl bg-white" data-testid="project-sync-dialog">
            <DialogHeader>
              <DialogTitle>
                Update project from newer estimation version
                {syncDialog.project && (
                  <span className="text-sm text-gray-500 font-normal ml-2">
                    · {syncDialog.project.project_number} · {syncDialog.project.name}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            {syncDialog.loading && (
              <div className="py-8 text-center text-gray-500 text-sm">Checking for newer versions…</div>
            )}

            {!syncDialog.loading && syncDialog.error && (
              <div className="py-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md3-md px-3">
                {syncDialog.error}
              </div>
            )}

            {!syncDialog.loading && !syncDialog.error && syncDialog.candidate && (
              <div className="space-y-3">
                <div className="rounded-md3-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm flex items-center justify-between">
                  <div>
                    <span className="text-gray-600">Estimation</span>{" "}
                    <span className="font-medium">{syncDialog.project?.estimation_number}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">v{syncDialog.candidate.current_version}</span>
                    <span className="mx-2 text-indigo-500">→</span>
                    <span className="font-semibold text-indigo-700">v{syncDialog.candidate.candidate_version}</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Project will bump to <span className="font-medium text-gray-700">v{
                      (() => {
                        const parts = (syncDialog.project?.project_version || "1.0").split(".");
                        return `${parts[0]}.${parseInt(parts[1] || 0) + 1}`;
                      })()
                    }</span>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-md3-md overflow-hidden">
                  <div className="grid grid-cols-3 gap-3 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-600 border-b">
                    <div>Field</div>
                    <div>Current</div>
                    <div>After sync</div>
                  </div>
                  {syncDialog.candidate.diff?.map((row) => (
                    <div
                      key={row.field}
                      className={`grid grid-cols-3 gap-3 px-3 py-2 text-sm border-b last:border-b-0 ${
                        row.changed ? "bg-amber-50" : ""
                      }`}
                    >
                      <div className="text-gray-700 font-medium">
                        {row.label}
                        {row.auto_computed && (
                          <span className="ml-2 text-[10px] font-normal text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md3-sm px-1.5 py-0.5 align-middle">
                            auto = detailing + engineering
                          </span>
                        )}
                      </div>
                      <div className={row.changed ? "text-gray-500 line-through" : "text-gray-800"}>
                        {row.old === null || row.old === "" || row.old === undefined ? "—" : String(row.old)}
                      </div>
                      <div className={row.changed ? "text-amber-800 font-medium" : "text-gray-400"}>
                        {row.new === null || row.new === "" || row.new === undefined ? (
                          row.changed ? "—" : ""
                        ) : (
                          String(row.new)
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md3-md px-3 py-2 leading-relaxed">
                  Your project remains valid. Only the reference numbers on the project header change.
                  <strong> Existing planning, timesheets, submittals, invoices, and budget calculations are untouched.</strong>
                  {" "}Empty fields on the newer estimation are skipped — we never blank out a populated project value.
                </div>

                {syncDialog.candidate.advisories?.length > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md3-md px-3 py-2 space-y-1">
                    {syncDialog.candidate.advisories.map((msg, i) => (
                      <div key={i}>⚠️ {msg}</div>
                    ))}
                  </div>
                )}

                {!syncDialog.candidate.diff?.some((r) => r.changed) && (
                  <div className="text-xs text-gray-500 italic">
                    Note: no reference field will actually change (the newer version has the same values or leaves them empty). Sync will still bump the project version and record an audit entry.
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setSyncDialog((d) => ({ ...d, open: false }))}
                className="rounded-md3-full"
                disabled={syncDialog.saving}
                data-testid="project-sync-cancel"
              >
                Not now
              </Button>
              {syncDialog.candidate?.has_candidate && (
                <Button
                  type="button"
                  onClick={confirmEstimationSync}
                  disabled={syncDialog.saving || syncDialog.loading}
                  className="rounded-md3-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="project-sync-confirm"
                >
                  {syncDialog.saving
                    ? "Syncing..."
                    : `Sync to v${syncDialog.candidate.candidate_version}`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}